import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePaymentMode } from '../contexts/PaymentModeContext';
import { HostDirectModeBanner } from '../components/HostDirectModeBanner';
import { useShadowMode } from '../contexts/ShadowModeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ResponsiveLayout, ResponsivePageHeader, ResponsiveStatsGrid } from '../components/ResponsiveLayout';
import LegalEditor from '../components/LegalEditor';
import LaunchPreparation from '../components/LaunchPreparation';
import UserDetailsDrawer from '../components/UserDetailsDrawer';
import StepUpAuthModal, { getValidStepUpToken, PreviewSecurityBanner } from '../components/StepUpAuthModal';
import RiskFlagsPanel from '../components/RiskFlagsPanel';
import TrackerAlertsPanel from '../components/TrackerAlertsPanel';
import { usePlatformFinanceSummary, formatAUD } from '../lib/usePlatformFinanceSummary';
import { 
  Car, DollarSign, Percent, Users, FileText, Settings, Shield, 
  Download, Bug, Crown, TrendingUp, Building, 
  AlertTriangle, Check, UserCog, UserPlus, Eye, CheckCircle, X,
  ChevronRight, ExternalLink, Calendar, Clock, Ban, RefreshCw, Edit,
  Trash2, UserX, Power, Rocket, MessageSquare, Image, Search, CreditCard, Wallet, Gavel,
  LayoutDashboard, Briefcase, Scale, Wrench, Mail, Globe, Locate, Handshake, ClipboardList,
  Gauge, KeyRound, Zap, Loader2, Sliders, MapPin, HeadphonesIcon
} from 'lucide-react';
import { ArrowRightLeft, Activity } from 'lucide-react';
import { toast } from 'sonner';
// Helper: Check if user has protected roles (cannot be deleted)
const isProtectedUser = (user) => {
  if (!user?.roles) return false;
  const protectedRoles = ['platform_owner', 'admin', 'super_admin'];
  return user.roles.some(r => protectedRoles.includes(r.role) && r.is_active);
};

// Helper: Get user's highest/primary role for display
const getPrimaryRole = (user) => {
  if (!user?.roles) return 'customer';
  const roleHierarchy = ['platform_owner', 'super_admin', 'admin', 'owner', 'customer'];
  for (const role of roleHierarchy) {
    if (user.roles.some(r => r.role === role && r.is_active)) return role;
  }
  return 'customer';
};

// Helper: Get role display config (icon, label, color)
const getRoleDisplay = (role) => {
  const configs = {
    platform_owner: { icon: '👑', label: 'Platform Owner', bgClass: 'bg-purple-500/20', textClass: 'text-purple-300' },
    super_admin: { icon: '⚡', label: 'Super Admin', bgClass: 'bg-blue-500/20', textClass: 'text-blue-400' },
    admin: { icon: '🛡️', label: 'Admin', bgClass: 'bg-indigo-500/20', textClass: 'text-indigo-400' },
    owner: { icon: '🚗', label: 'Host', bgClass: 'bg-emerald-500/20', textClass: 'text-emerald-400' },
    customer: { icon: '👤', label: 'Customer', bgClass: 'bg-white/10', textClass: 'text-white/70' }
  };
  return configs[role] || configs.customer;
};

const PlatformOwnerDashboard = () => {
  const { user, api, logout } = useAuth();
  const { isHostDirect } = usePaymentMode();
  const { startShadowSession } = useShadowMode();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Payment Finance - Single source of truth for platform financials
  const [financeRangeDays, setFinanceRangeDays] = useState(30);
  const { 
    data: financeData, 
    loading: financeLoading, 
    error: financeError, 
    refetch: refetchFinance 
  } = usePlatformFinanceSummary(financeRangeDays, true);

  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantUserId, setGrantUserId] = useState('');
  const [grantType, setGrantType] = useState('admin');
  const [newCommission, setNewCommission] = useState(15);
  const [savingCommission, setSavingCommission] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  
  // Users & Verification state
  const [allUsers, setAllUsers] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  // PART D: users tab scalability — search + pagination state
  const [usersSearchQuery, setUsersSearchQuery] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const USERS_PAGE_SIZE = 25;
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // P0: Registration Expiry Management state
  const [expiringVehicles, setExpiringVehicles] = useState([]);
  
  // Upgrade Requests state
  const [upgradeRequests, setUpgradeRequests] = useState([]);
  const [upgradeRequestsCounts, setUpgradeRequestsCounts] = useState({});
  const [upgradeRequestsLoading, setUpgradeRequestsLoading] = useState(false);
  const [upgradeRequestsFilter, setUpgradeRequestsFilter] = useState('all');
  
  // Ranking Config state
  const [rankingConfig, setRankingConfig] = useState(null);
  const [rankingConfigLoading, setRankingConfigLoading] = useState(false);
  const [rankingConfigSaving, setRankingConfigSaving] = useState(false);
  
  // GPS Alerts (PO) state
  const [poGpsAlerts, setPoGpsAlerts] = useState([]);
  const [poGpsAlertsCounts, setPoGpsAlertsCounts] = useState({});
  const [poGpsAlertsLoading, setPoGpsAlertsLoading] = useState(false);
  const [poGpsAlertsFilter, setPoGpsAlertsFilter] = useState('active');
  
  // Support Tickets (PO) state
  const [poSupportTickets, setPoSupportTickets] = useState([]);
  const [poSupportTicketsCounts, setPoSupportTicketsCounts] = useState({});
  const [poSupportTicketsLoading, setPoSupportTicketsLoading] = useState(false);
  const [poSupportTicketsFilter, setPoSupportTicketsFilter] = useState('open');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showRegoOverrideModal, setShowRegoOverrideModal] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  
  // P0: Disputes Management state
  const [disputes, setDisputes] = useState([]);
  const [disputeStatusFilter, setDisputeStatusFilter] = useState('');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showDisputeDetailModal, setShowDisputeDetailModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolutionText, setResolutionText] = useState('');
  const [resolutionAmount, setResolutionAmount] = useState('');
  const [resolutionFavor, setResolutionFavor] = useState('partial');
  const [adminNote, setAdminNote] = useState('');

  // P0: Overdue Trips Management state
  const [overdueTrips, setOverdueTrips] = useState([]);
  const [overdueDataMeta, setOverdueDataMeta] = useState({ test_data_excluded: 0, data_source: '' });
  const [overdueLoading, setOverdueLoading] = useState(false);
  const [selectedOverdueBooking, setSelectedOverdueBooking] = useState(null);
  const [showResolveOverdueModal, setShowResolveOverdueModal] = useState(false);
  const [overdueResolveReason, setOverdueResolveReason] = useState('');
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [overdueNote, setOverdueNote] = useState('');

  // P0: Legal Pages Editor state
  const [legalPagesStatus, setLegalPagesStatus] = useState([]);
  const [selectedLegalPage, setSelectedLegalPage] = useState(null);
  const [showLegalEditorModal, setShowLegalEditorModal] = useState(false);

  // Delete User/Admin state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteEligibility, setDeleteEligibility] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Step-Up Auth state
  const [showStepUp, setShowStepUp] = useState(false);
  const [stepUpAction, setStepUpAction] = useState(null); // { label, callback }

  // P0: Evidence Viewer state
  const [evidenceBookingId, setEvidenceBookingId] = useState('');
  const [evidenceData, setEvidenceData] = useState(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  // Listen for user-updated event to refresh selected user data
  useEffect(() => {
    const handleUserUpdated = async (event) => {
      const { userId } = event.detail || {};
      // If we have a selected user and it matches the updated user, refresh their data
      if (selectedUser && userId && selectedUser.user_id === userId) {
        try {
          // Fetch fresh user data from the server
          const res = await api.get(`/admin/users/${userId}`);
          if (res.data) {
            setSelectedUser(res.data);
          }
        } catch (err) {
          console.error('Failed to refresh user data:', err);
        }
      }
    };

    window.addEventListener('user-updated', handleUserUpdated);
    return () => window.removeEventListener('user-updated', handleUserUpdated);
  }, [selectedUser, api]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const res = await api.get('/platform-owner/dashboard');
        setDashboardData(res.data);
        setNewCommission(
          res.data.commission?.platform_fee_percentage ??
          (isHostDirect ? 0 : 15)
        );
      } else if (activeTab === 'admins') {
        const res = await api.get('/platform-owner/all-admins');
        setAdmins(res.data.admins || []);
      } else if (activeTab === 'stripe') {
        // Payment dashboard tab - no data to fetch
      } else if (activeTab === 'audit') {
        const res = await api.get('/platform-owner/audit-logs?limit=200');
        setAuditLogs(res.data.logs || []);
      } else if (activeTab === 'users') {
        // Fetch all users and ONLY pending verifications
        const [usersRes, verificationsRes] = await Promise.all([
          api.get('/admin/users'),
          api.get('/admin/verifications?pending_only=true')
        ]);
        setAllUsers(usersRes.data.users || []);
        setPendingVerifications(verificationsRes.data.verifications || []);
      } else if (activeTab === 'rego') {
        // P0: Fetch vehicles with expiring registrations
        const res = await api.get('/platform-owner/expiring-registrations');
        setExpiringVehicles(res.data.vehicles || []);
      } else if (activeTab === 'disputes') {
        // P0: Fetch disputes dashboard
        const url = disputeStatusFilter 
          ? `/admin/disputes/dashboard?status=${disputeStatusFilter}` 
          : '/admin/disputes/dashboard';
        const res = await api.get(url);
        setDisputes(res.data.disputes || []);
      } else if (activeTab === 'overdue') {
        // P0: Fetch overdue trips
        setOverdueLoading(true);
        const res = await api.get('/admin/overdue-trips');
        setOverdueTrips(res.data.overdue_trips || []);
        setOverdueDataMeta({
          test_data_excluded: res.data.test_data_excluded || 0,
          data_source: res.data.data_source || 'APP_DB'
        });
        setOverdueLoading(false);
      } else if (activeTab === 'legal') {
        // P0: Fetch legal pages status
        const res = await api.get('/platform-owner/legal');
        setLegalPagesStatus(res.data.pages || []);
      } else if (activeTab === 'upgrade-requests') {
        setUpgradeRequestsLoading(true);
        const url = upgradeRequestsFilter !== 'all' 
          ? `/admin/upgrade-requests?status=${upgradeRequestsFilter}` 
          : '/admin/upgrade-requests';
        const res = await api.get(url);
        setUpgradeRequests(res.data.requests || []);
        setUpgradeRequestsCounts(res.data.counts || {});
        setUpgradeRequestsLoading(false);
      } else if (activeTab === 'ranking-controls') {
        setRankingConfigLoading(true);
        try {
          const res = await api.get('/admin/ranking-config');
          setRankingConfig(res.data);
        } catch { /* fallback handled in UI */ }
        setRankingConfigLoading(false);
      } else if (activeTab === 'gps-alerts') {
        setPoGpsAlertsLoading(true);
        try {
          const url = poGpsAlertsFilter !== 'all' ? `/platform-owner/gps-alerts?status=${poGpsAlertsFilter}` : '/platform-owner/gps-alerts';
          const res = await api.get(url);
          setPoGpsAlerts(res.data.alerts || []);
          setPoGpsAlertsCounts(res.data.counts || {});
        } catch { /* empty state */ }
        setPoGpsAlertsLoading(false);
      } else if (activeTab === 'support-tickets') {
        setPoSupportTicketsLoading(true);
        try {
          const url = poSupportTicketsFilter !== 'all' ? `/admin/support-tickets?status=${poSupportTicketsFilter}` : '/admin/support-tickets';
          const res = await api.get(url);
          setPoSupportTickets(res.data.tickets || []);
          setPoSupportTicketsCounts(res.data.counts || {});
        } catch { /* empty state */ }
        setPoSupportTicketsLoading(false);
      } else if (activeTab === 'settings') {
        // Fetch real system status
        try {
          const res = await api.get('/platform-owner/system-status');
          setSystemStatus(res.data);
        } catch { /* non-critical */ }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied - Platform Owner only');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCommission = async () => {
    setSavingCommission(true);
    try {
      await api.put('/platform-owner/commission', { platform_fee_percentage: newCommission });
      toast.success('Commission updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update commission');
    } finally {
      setSavingCommission(false);
    }
  };

  /**
   * Require step-up authentication before executing a sensitive action.
   * If a valid step-up token exists (within 10 min window), skip the modal.
   */
  const requireStepUp = (actionLabel, callback) => {
    const existing = getValidStepUpToken();
    if (existing) {
      callback(existing);
    } else {
      setStepUpAction({ label: actionLabel, callback });
      setShowStepUp(true);
    }
  };

  const handleStepUpSuccess = (token) => {
    setShowStepUp(false);
    if (stepUpAction?.callback) stepUpAction.callback(token);
    setStepUpAction(null);
  };

  const _executeGrantRole = async () => {
    if (!grantUserId.trim()) return;
    try {
      const endpoint = grantType === 'super_admin' 
        ? `/platform-owner/grant-super-admin/${grantUserId}`
        : `/super-admin/admins/${grantUserId}/grant`;
      await api.post(endpoint);
      toast.success(`${grantType === 'super_admin' ? 'Super Admin' : 'Admin'} role granted`);
      setShowGrantModal(false);
      setGrantUserId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant role');
    }
  };

  const handleGrantRole = () => {
    requireStepUp('grant an admin role', _executeGrantRole);
  };

  const handleRevokeRole = async (userId, role) => {
    try {
      const endpoint = role === 'super_admin'
        ? `/platform-owner/revoke-super-admin/${userId}`
        : `/super-admin/admins/${userId}/revoke`;
      await api.post(endpoint);
      toast.success(`${role === 'super_admin' ? 'Super Admin' : 'Admin'} role revoked`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke role');
    }
  };

  const handleExportAuditLogs = async () => {
    try {
      const res = await api.get('/platform-owner/audit-logs/export');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('Audit logs exported');
    } catch (error) {
      toast.error('Failed to export audit logs');
    }
  };

  // Verification Actions
  const handleApproveVerification = async (verificationId, licenceExpiryDate) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/verifications/${verificationId}/approve`, {
        licence_expiry_date: licenceExpiryDate
      });
      toast.success('Verification approved' + (licenceExpiryDate ? ` - Licence expires ${licenceExpiryDate}` : ''));
      setShowUserModal(false);
      setSelectedUser(null);
      setSelectedVerification(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to approve verification');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectVerification = async () => {
    if (!selectedVerification) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/verifications/${selectedVerification.verification_id}/reject`, {
        reason: rejectReason
      });
      toast.success('Verification rejected');
      setShowRejectModal(false);
      setShowUserModal(false);
      setSelectedUser(null);
      setSelectedVerification(null);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject verification');
    } finally {
      setActionLoading(false);
    }
  };

  const openUserDetails = async (userData) => {
    setSelectedUser(userData);
    // Find pending verification for this user
    const userVerification = pendingVerifications.find(v => v.user_id === userData.user_id);
    if (userVerification) {
      setSelectedVerification(userVerification);
    } else {
      // Try to fetch user's verification (any status) for display purposes
      try {
        const res = await api.get(`/admin/verifications?user_id=${userData.user_id}`);
        const allVerifs = res.data.verifications || [];
        const userVerif = allVerifs.find(v => v.user_id === userData.user_id);
        setSelectedVerification(userVerif || null);
      } catch {
        setSelectedVerification(null);
      }
    }
    setShowUserModal(true);
  };

  // P0 CRITICAL: Get verification document with authenticated presigned URL
  // Includes 1 retry max if first request fails
  const fetchDocumentUrl = async (userId, docType, retry = 0) => {
    try {
      const response = await api.get(`/admin/verifications/${userId}/documents/${docType}`);
      if (!response.data.url) {
        throw new Error('No URL in response');
      }
      return response.data.url;
    } catch (error) {
      console.error(`Error fetching ${docType} document (attempt ${retry + 1}):`, error);
      if (retry < 1) {
        // Retry once
        await new Promise(r => setTimeout(r, 500));
        return fetchDocumentUrl(userId, docType, retry + 1);
      }
      toast.error(`Failed to load ${docType} document. Please try again.`);
      return null;
    }
  };

  // Open document in new tab with authenticated URL
  const _executeOpenDocument = async (userId, docType) => {
    toast.info(`Loading ${docType}...`);
    const url = await fetchDocumentUrl(userId, docType);
    if (url) {
      window.open(url, '_blank');
      toast.success('Document opened');
    }
  };

  const openDocument = (userId, docType) => {
    requireStepUp('view identity documents', () => _executeOpenDocument(userId, docType));
  };

  // Generate presigned URL for verification documents (DEPRECATED - use fetchDocumentUrl)
  const getDocUrl = (s3KeyOrUrl) => {
    if (!s3KeyOrUrl) return null;
    // If it's already a full URL, return as-is
    if (s3KeyOrUrl.startsWith('http')) return s3KeyOrUrl;
    // If it's an S3 key, construct the API URL that will redirect to presigned URL
    const filename = s3KeyOrUrl.split('/').pop();
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/verifications/${filename}`;
  };

  // P0: Overdue Protection handlers
  const handleResolveOverdue = async () => {
    if (!selectedOverdueBooking || !overdueResolveReason.trim()) {
      toast.error('Please provide a resolution reason');
      return;
    }
    try {
      await api.post(`/admin/bookings/${selectedOverdueBooking.booking_id}/resolve-overdue`, {
        reason: overdueResolveReason
      });
      toast.success('Overdue status resolved');
      setShowResolveOverdueModal(false);
      setOverdueResolveReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve overdue status');
    }
  };

  const handleAddOverdueNote = async () => {
    if (!selectedOverdueBooking || !overdueNote.trim()) {
      toast.error('Please provide a note');
      return;
    }
    try {
      await api.post(`/admin/bookings/${selectedOverdueBooking.booking_id}/overdue-note`, {
        note: overdueNote
      });
      toast.success('Note added');
      setShowAddNoteModal(false);
      setOverdueNote('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleExportIncidentPack = async (bookingId) => {
    try {
      const res = await api.get(`/admin/bookings/${bookingId}/export-incident-pack`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `incident_pack_${bookingId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Incident pack downloaded');
    } catch (error) {
      toast.error('Failed to export incident pack');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Delete User/Admin handlers
  const openDeleteModal = async (userData, type = 'user') => {
    setDeleteTarget({ ...userData, type });
    setDeleteConfirmText('');
    setDeleteEligibility(null);
    setShowDeleteModal(true);
    
    // Check delete eligibility
    try {
      const res = await api.get(`/platform-owner/users/${userData.user_id}/delete-eligibility`);
      setDeleteEligibility(res.data);
    } catch (error) {
      setDeleteEligibility({
        can_delete: false,
        reasons: ['Failed to check eligibility: ' + (error.response?.data?.detail || error.message)],
        suggestion: 'disable'
      });
    }
  };

  const _executeDeleteUser = async () => {
    if (!deleteTarget || deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleteLoading(true);
    try {
      await api.delete(`/platform-owner/users/${deleteTarget.user_id}?confirm=DELETE`);
      toast.success('User deleted successfully');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteUser = () => {
    // P0 FIX: Close the delete dialog FIRST to release Radix Dialog's focus trap.
    // Without this, the StepUpAuthModal's password input is blocked by the focus trap.
    // State (deleteTarget, deleteConfirmText) persists in memory across the close.
    setShowDeleteModal(false);
    requireStepUp('delete this user', _executeDeleteUser);
  };

  const handleDisableUser = async (userId) => {
    try {
      await api.post(`/platform-owner/users/${userId}/disable`);
      toast.success('User disabled');
      setShowDeleteModal(false);
      setShowUserModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable user');
    }
  };

  const handleEnableUser = async (userId) => {
    try {
      await api.post(`/platform-owner/users/${userId}/enable`);
      toast.success('User enabled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable user');
    }
  };

  const handleNavClick = (id) => {
    if (id === 'debug') {
      navigate('/dev-debug');
    } else if (id === 'financial') {
      navigate('/financial-enforcement');
    } else if (id === 'support-tickets') {
      setActiveTab(id);
    } else if (id === 'gps-alerts') {
      setActiveTab(id);
    } else if (id === 'payouts') {
      navigate('/platform-owner/payouts');
    } else if (id === 'claims') {
      navigate('/admin/claims');
    } else if (id === 'vehicle-mgmt') {
      navigate('/platform-owner/vehicles');
    } else if (id === 'stripe') {
      // Open Airwallex Dashboard in new tab
      window.open('https://www.airwallex.com/app', '_blank', 'noopener,noreferrer');
    } else if (id === 'email-centre') {
      navigate('/platform-owner/email-centre');
    } else if (id === 'social-links') {
      navigate('/platform-owner/social-links');
    } else if (id === 'bookings') {
      navigate('/platform-owner/bookings');
    } else if (id === 'recovery') {
      navigate('/platform-owner/recovery');
    } else if (id === 'charge-caps') {
      navigate('/platform-owner/charge-caps');
    } else if (id === 'payment-auths') {
      navigate('/platform-owner/payment-authorizations');
    } else if (id === 'host-financials') {
      navigate('/admin/host-financials');
    } else if (id === 'host-agreements') {
      navigate('/admin/agreements');
    } else if (id === 'security') {
      navigate('/platform-owner/security');
    } else if (id === 'security-activity') {
      navigate('/platform-owner/security-activity');
    } else if (id === 'transfer-ownership') {
      navigate('/platform-owner/transfer-ownership');
    } else if (id === 'ops-briefing') {
      navigate('/platform-owner/ops-briefing');
    } else {
      setActiveTab(id);
    }
  };

  // Shadow Session (View As) handler
  const handleStartShadowSession = async (targetUserId, reason = 'support') => {
    try {
      toast.info('Starting shadow session...', { duration: 2000 });
      await startShadowSession(targetUserId, reason);
      // The startShadowSession will redirect to the appropriate page
    } catch (err) {
      console.error('Failed to start shadow session:', err);
      toast.error(err.response?.data?.detail || 'Failed to start shadow session');
    }
  };

  const sidebarGroups = [
    {
      title: 'Overview',
      icon: LayoutDashboard,
      items: [
        { id: 'dashboard', icon: Crown, label: 'Dashboard' },
        { id: 'ops-briefing', icon: Activity, label: 'Ops Briefing' },
        { id: 'support-tickets', icon: MessageSquare, label: 'Support Tickets' },
      ]
    },
    {
      title: 'Operations',
      icon: Briefcase,
      items: [
        { id: 'vehicle-mgmt', icon: Car, label: 'Vehicle Management' },
        { id: 'users', icon: Users, label: 'Users' },
        { id: 'upgrade-requests', icon: TrendingUp, label: 'Upgrade Requests' },
        { id: 'ranking-controls', icon: Sliders, label: 'Ranking Controls' },
        { id: 'gps-alerts', icon: Locate, label: 'GPS Alerts' },
        { id: 'claims', icon: Gavel, label: 'Claims' },
        { id: 'bookings', icon: ClipboardList, label: 'Bookings' },
        { id: 'recovery', icon: Locate, label: 'Recovery Mode' },
        { id: 'evidence', icon: Image, label: 'Evidence Viewer' },
        { id: 'rego', icon: Calendar, label: 'Registration Expiry' },
        { id: 'overdue', icon: Clock, label: 'Overdue Trips' },
        { id: 'disputes', icon: AlertTriangle, label: 'Disputes' },
      ]
    },
    {
      title: 'Financial',
      icon: DollarSign,
      items: [
        { id: 'financial', icon: TrendingUp, label: 'Financial Enforcement' },
        { id: 'host-financials', icon: Building, label: 'Host Financials' },
        { id: 'charge-caps', icon: Gauge, label: 'Charge Caps' },
        { id: 'payment-auths', icon: KeyRound, label: 'Payment Authorizations' },
        { id: 'payouts', icon: Wallet, label: 'Payouts' },
        { id: 'stripe', icon: CreditCard, label: 'Payment Dashboard', external: true },
      ]
    },
    {
      title: 'Governance',
      icon: Scale,
      items: [
        { id: 'legal', icon: Shield, label: 'Legal Pages' },
        { id: 'audit', icon: FileText, label: 'Audit Logs' },
        { id: 'admins', icon: UserCog, label: 'Admin Management' },
        { id: 'host-agreements', icon: Handshake, label: 'Host Agreements' },
        { id: 'email-centre', icon: Mail, label: 'Email Centre' },
        { id: 'settings', icon: Settings, label: 'Platform Settings' },
        { id: 'social-links', icon: Globe, label: 'Social Links' },
        { id: 'security', icon: Shield, label: 'Security' },
        { id: 'security-activity', icon: Eye, label: 'Security Activity' },
        { id: 'transfer-ownership', icon: ArrowRightLeft, label: 'Transfer Ownership' },
      ]
    },
    {
      title: 'Launch / Diagnostics',
      icon: Wrench,
      items: [
        { id: 'launch', icon: Rocket, label: 'Launch Preparation' },
        { id: 'debug', icon: Bug, label: 'Platform Command Centre' },
      ]
    },
  ];

  const LoadingSpinner = () => (
    <div className="flex justify-center py-12 sm:py-20">
      <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-purple-500"></div>
    </div>
  );

  return (
    <>
    <ResponsiveLayout
      sidebarGroups={sidebarGroups}
      activeTab={activeTab}
      onTabChange={handleNavClick}
      headerTitle="Platform Owner"
      headerIcon={Crown}
      user={user}
      onLogout={handleLogout}
    >
      <PreviewSecurityBanner />
      <HostDirectModeBanner variant="subtle" context="generic" className="mx-4 mt-4 sm:mx-6" />
      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader
            title="Platform Dashboard"
            subtitle="Complete platform overview and control"
            badge={
              <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 px-3 py-1 text-xs sm:text-sm">
                <Crown className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5" />
                Platform Owner
              </Badge>
            }
          />

          {loading ? <LoadingSpinner /> : dashboardData && (
            <>
              {/* Payment Revenue Summary - Range Selector */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm text-white/80">Revenue</span>
                </div>
                <div className="flex items-center gap-2">
                  <select 
                    value={financeRangeDays}
                    onChange={(e) => setFinanceRangeDays(Number(e.target.value))}
                    className="bg-white/10 border border-white/20 rounded px-2 py-1.5 text-xs text-white"
                    data-testid="finance-range-selector"
                  >
                    <option value={7}>7 days</option>
                    <option value={30}>30 days</option>
                    <option value={90}>90 days</option>
                    <option value={365}>1 year</option>
                  </select>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => refetchFinance(financeRangeDays)}
                    disabled={financeLoading}
                    className="text-white/60 hover:text-white p-2"
                    data-testid="refresh-finance-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${financeLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              <ResponsiveStatsGrid>
                {/* Platform Earnings (Gross) — neutralised in host_direct mode */}
                <Card className="bg-emerald-900/30 border-emerald-500/30 backdrop-blur">
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-emerald-400 mb-2 sm:mb-3 lg:mb-4" />
                    {isHostDirect ? (
                      <>
                        <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-emerald-300" data-testid="platform-earnings-gross">
                          —
                        </p>
                        <p className="text-emerald-400/80 text-xs sm:text-sm">Platform Earnings</p>
                        <p className="text-xs italic text-emerald-400/60 mt-1" data-testid="platform-earnings-hostdirect-note">
                          No platform fee (direct payment mode)
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-emerald-300" data-testid="platform-earnings-gross">
                          {financeLoading ? '...' : financeData ? formatAUD(financeData.totals.platform_fees_gross, false) : '—'}
                        </p>
                        <p className="text-emerald-400/80 text-xs sm:text-sm">Platform Earnings (Gross)</p>
                        {financeData && (
                          <p className="text-xs text-white/50 mt-1">
                            Net: {formatAUD(financeData.totals.platform_fees_net, false)}
                          </p>
                        )}
                        {financeError && <p className="text-xs text-amber-400 mt-1">Unable to load</p>}
                      </>
                    )}
                  </CardContent>
                </Card>
                
                {/* Total Revenue (GMV) */}
                <Card className="bg-blue-900/30 border-blue-500/30 backdrop-blur">
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-blue-400 mb-2 sm:mb-3 lg:mb-4" />
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-300" data-testid="total-revenue-gmv">
                      {financeLoading ? '...' : financeData ? formatAUD(financeData.totals.total_charges_gross, false) : '—'}
                    </p>
                    <p className="text-blue-400/80 text-xs sm:text-sm">Total Revenue (GMV)</p>
                    {financeData && (
                      <p className="text-xs text-white/50 mt-1">
                        {financeData.counts.paid_bookings} paid bookings
                      </p>
                    )}
                    {financeError && <p className="text-xs text-amber-400 mt-1">Unable to load</p>}
                  </CardContent>
                </Card>
                
                <Card className="bg-white/5 border-white/10 backdrop-blur">
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    <Users className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-purple-400 mb-2 sm:mb-3 lg:mb-4" />
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold">{dashboardData.stats.total_users}</p>
                    <p className="text-white/60 text-xs sm:text-sm">Total Users</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10 backdrop-blur">
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    <Car className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-cyan-400 mb-2 sm:mb-3 lg:mb-4" />
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold">{dashboardData.stats.total_vehicles}</p>
                    <p className="text-white/60 text-xs sm:text-sm">Listed Vehicles</p>
                  </CardContent>
                </Card>
              </ResponsiveStatsGrid>

              {/* Finance error message */}
              {financeError && (
                <div className="text-center text-amber-400/80 text-sm py-2 bg-amber-500/10 rounded-lg mx-auto max-w-md" data-testid="finance-error-banner">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Unable to load financial data: {financeError}
                </div>
              )}
              {!financeLoading && !financeError && financeData && financeData.totals.total_charges_gross === 0 && (
                <div className="text-center text-white/50 text-sm py-2">
                  No payment data found for selected range
                </div>
              )}

              {/* View Full Breakdown Link */}
              <div className="flex justify-end mb-4">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/financial-enforcement')}
                  className="text-emerald-400 hover:text-emerald-300 text-xs"
                >
                  View full breakdown <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                {/* Risk Flags Panel - P0/P1 */}
                <div className="sm:col-span-2 lg:col-span-2">
                  <RiskFlagsPanel />
                </div>
                
                {/* GPS Tracker Alerts Panel - P2 Security */}
                <div className="lg:col-span-1">
                  <TrackerAlertsPanel />
                </div>

                <Card className="bg-white/5 border-white/10 backdrop-blur">
                  <CardHeader className="pb-2 p-4 sm:p-6 sm:pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg">
                      <Percent className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                      Commission Rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 sm:space-y-4 p-4 pt-2 sm:p-6 sm:pt-2">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        step="0.5"
                        value={newCommission}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          setNewCommission(isNaN(v) ? 0 : v);
                        }}
                        className="flex-1 bg-white/10 border-white/20 text-white h-10 sm:h-11"
                        data-testid="commission-input"
                      />
                      <span className="text-white/60 text-sm">%</span>
                    </div>
                    {isHostDirect && (
                      <p
                        className="text-xs text-amber-300/90"
                        data-testid="commission-hostdirect-hint-dashboard"
                      >
                        {newCommission === 0
                          ? '0% platform fee — inactive in direct payment mode.'
                          : 'Platform fees are currently inactive in direct payment mode. You can set this to 0% or configure it for future platform payment use.'}
                      </p>
                    )}
                    <Button 
                      onClick={handleSaveCommission}
                      disabled={savingCommission}
                      className="w-full bg-purple-600 hover:bg-purple-700 h-10 sm:h-11 text-sm"
                      data-testid="update-commission-btn"
                    >
                      {savingCommission ? 'Saving...' : 'Update Commission'}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10 backdrop-blur">
                  <CardHeader className="pb-2 p-4 sm:p-6 sm:pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg">
                      <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
                      Pending Verifications
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-2 sm:p-6 sm:pt-2">
                    <p className="text-white/60 mb-3 sm:mb-4 text-xs sm:text-sm">
                      {dashboardData.stats.pending_verifications || 0} users awaiting verification
                    </p>
                    <Button 
                      onClick={() => setActiveTab('users')}
                      variant="outline"
                      className="w-full border-white/20 text-white hover:bg-white/10 h-10 sm:h-11 text-sm"
                      data-testid="view-users-btn"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      View Users
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10 backdrop-blur sm:col-span-2 lg:col-span-1">
                  <CardHeader className="pb-2 p-4 sm:p-6 sm:pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg">
                      <UserCog className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                      Admin Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-2 sm:p-6 sm:pt-2">
                    <p className="text-white/60 mb-3 sm:mb-4 text-xs sm:text-sm">
                      Grant or revoke admin privileges
                    </p>
                    <Button 
                      onClick={() => { setGrantType('admin'); setShowGrantModal(true); }}
                      className="w-full bg-blue-600 hover:bg-blue-700 h-10 sm:h-11 text-sm"
                      data-testid="grant-role-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Grant Role
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {/* Launch Preparation Tab */}
      {activeTab === 'launch' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader 
            title="Launch Preparation" 
            subtitle="Pre-launch data cleanup tools for production readiness"
          />
          <LaunchPreparation api={api} />
        </div>
      )}

      {/* Users Tab - NEW */}
      {activeTab === 'users' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader
            title="User Management"
            subtitle="Manage users and identity verification"
          />

          {/* Pending Verifications Section
              FIX: Source of truth is the /api/admin/verifications?pending_only=true
              endpoint, NOT users.verification_status (which stays at 'unverified'
              for guest-checkout users while their verifications.status is 'pending').
              We hydrate display info from allUsers when available, but the queue
              membership is driven by the verifications collection. */}
          {/* Note: Platform Owner/Admin/Super Admin accounts are excluded (they don't need verification) */}
          {(() => {
            const usersById = new Map(allUsers.map(u => [u.user_id, u]));
            const protectedRoles = ['platform_owner', 'admin', 'super_admin'];
            const pendingItems = (pendingVerifications || [])
              .map(v => ({ verification: v, user: usersById.get(v.user_id) }))
              // Skip orphan verifications (user record removed) and protected accounts
              .filter(({ user }) => {
                if (!user) return false;
                const hasProtectedRole = user.roles?.some(
                  r => protectedRoles.includes(r.role) && r.is_active
                );
                return !hasProtectedRole;
              });
            if (pendingItems.length === 0) return null;
            return (
              <Card className="bg-orange-500/10 border-orange-500/30">
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg text-orange-400">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
                    Pending Verifications ({pendingItems.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                  <div className="space-y-3">
                    {pendingItems.map(({ verification: userVerification, user: u }) => {
                      return (
                        <div 
                          key={u.user_id} 
                          className="p-3 sm:p-4 bg-white/5 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer hover:bg-white/10 transition-colors"
                          onClick={() => {
                            setSelectedUser(u);
                            setSelectedVerification(userVerification || null);
                            setShowUserModal(true);
                          }}
                          data-testid={`pending-user-${u.user_id}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-orange-500/30 rounded-full flex items-center justify-center flex-shrink-0">
                              <Shield className="w-5 h-5 text-orange-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm sm:text-base truncate">{u.full_name || 'User'}</p>
                              <p className="text-xs sm:text-sm text-white/60 truncate">{u.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pl-12 sm:pl-0">
                            <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Pending Review</Badge>
                            <Eye className="w-4 h-4 text-white/40" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* All Users Section
              PART D: search by name/email/role/verification_status + paginated.
              Pending Verifications card above is NOT affected by these filters
              (it's always-visible, drawn from a separate dataset). */}
          {(() => {
            const q = (usersSearchQuery || '').trim().toLowerCase();
            const filtered = q ? allUsers.filter(u => {
              const name = (u.full_name || '').toLowerCase();
              const email = (u.email || '').toLowerCase();
              const status = (u.verification_status || '').toLowerCase();
              const roles = (u.roles || []).map(r => (r.role || '').toLowerCase()).join(' ');
              return name.includes(q) || email.includes(q) || status.includes(q) || roles.includes(q);
            }) : allUsers;
            const totalFiltered = filtered.length;
            const totalPages = Math.max(1, Math.ceil(totalFiltered / USERS_PAGE_SIZE));
            const safePage = Math.min(usersPage, totalPages);
            const startIdx = (safePage - 1) * USERS_PAGE_SIZE;
            const endIdx = Math.min(startIdx + USERS_PAGE_SIZE, totalFiltered);
            const visibleUsers = filtered.slice(startIdx, endIdx);
            return (
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                  All Users ({allUsers.length})
                </CardTitle>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <input
                    type="search"
                    value={usersSearchQuery}
                    onChange={(e) => { setUsersSearchQuery(e.target.value); setUsersPage(1); }}
                    placeholder="Search name, email, role, status…"
                    data-testid="users-search-input"
                    className="flex-1 sm:w-72 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-purple-400"
                  />
                </div>
              </div>
              {totalFiltered > 0 && (
                <div className="mt-2 text-xs text-white/60" data-testid="users-pagination-summary">
                  Showing {startIdx + 1}–{endIdx} of {totalFiltered}{q ? ` (filtered from ${allUsers.length})` : ''}
                </div>
              )}
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
              {loading ? <LoadingSpinner /> : visibleUsers.length === 0 ? (
                <p className="text-center py-8 sm:py-12 text-white/60 text-sm" data-testid="users-empty-state">
                  {q ? 'No users match your search.' : 'No users found'}
                </p>
              ) : (
                <>
                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-3">
                    {visibleUsers.map((u) => {
                      // Check if this is a protected account
                      const protectedRoles = ['platform_owner', 'admin', 'super_admin'];
                      const isProtectedAccount = u.roles?.some(r => protectedRoles.includes(r.role) && r.is_active);
                      
                      return (
                      <div 
                        key={u.user_id} 
                        className="p-3 sm:p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => openUserDetails(u)}
                        data-testid={`user-card-${u.user_id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate">{u.full_name}</p>
                            <p className="text-xs text-white/60 truncate">{u.email}</p>
                          </div>
                          {/* Show verification status only for non-protected accounts */}
                          {isProtectedAccount ? (
                            <Badge className="text-[10px] ml-2 flex-shrink-0 bg-purple-500/20 text-purple-300">
                              Protected
                            </Badge>
                          ) : (
                            <Badge className={`text-[10px] ml-2 flex-shrink-0 ${
                              u.verification_status === 'verified' ? 'bg-green-500/20 text-green-400' :
                              u.verification_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              u.verification_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                              'bg-white/10 text-white/60'
                            }`}>
                              {u.verification_status || 'unverified'}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-1 flex-wrap">
                            {(() => {
                              const primaryRole = getPrimaryRole(u);
                              const roleConfig = getRoleDisplay(primaryRole);
                              return (
                                <Badge className={`text-[10px] ${roleConfig.bgClass} ${roleConfig.textClass}`}>
                                  <span className="mr-1">{roleConfig.icon}</span>
                                  {roleConfig.label}
                                </Badge>
                              );
                            })()}
                          </div>
                          <span className="text-[10px] text-white/40">
                            {new Date(u.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    )})}
                  </div>

                  {/* Desktop Table View */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Name</th>
                          <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Email</th>
                          <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Roles</th>
                          <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Created</th>
                          <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Verification</th>
                          <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleUsers.map((u) => {
                          // Check if this is a protected account
                          const protectedRoles = ['platform_owner', 'admin', 'super_admin'];
                          const isProtectedAccount = u.roles?.some(r => protectedRoles.includes(r.role) && r.is_active);
                          
                          return (
                          <tr 
                            key={u.user_id} 
                            className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                            onClick={() => openUserDetails(u)}
                            data-testid={`user-row-${u.user_id}`}
                          >
                            <td className="py-3 px-4 text-sm">{u.full_name}</td>
                            <td className="py-3 px-4 text-sm text-white/60">{u.email}</td>
                            <td className="py-3 px-4">
                              <div className="flex gap-1 flex-wrap">
                                {(() => {
                                  const primaryRole = getPrimaryRole(u);
                                  const roleConfig = getRoleDisplay(primaryRole);
                                  return (
                                    <Badge className={`text-xs ${roleConfig.bgClass} ${roleConfig.textClass}`}>
                                      <span className="mr-1">{roleConfig.icon}</span>
                                      {roleConfig.label}
                                    </Badge>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-white/60">
                              {new Date(u.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4">
                              {/* Show verification status only for non-protected accounts */}
                              {isProtectedAccount ? (
                                <Badge className="text-xs bg-purple-500/20 text-purple-300">
                                  Protected
                                </Badge>
                              ) : (
                                <Badge className={`text-xs ${
                                  u.verification_status === 'verified' ? 'bg-green-500/20 text-green-400' :
                                  u.verification_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                  u.verification_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                  'bg-white/10 text-white/60'
                                }`}>
                                  {u.verification_status || 'unverified'}
                                </Badge>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="border-white/20 text-white hover:bg-white/10 h-8 text-xs"
                                  onClick={(e) => { e.stopPropagation(); openUserDetails(u); }}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  View
                                </Button>
                                {/* Delete button: Show for ALL non-protected users in preview */}
                                {!isProtectedUser(u) && (
                                  <Button 
                                    size="sm" 
                                    variant="destructive" 
                                    className="h-8 text-xs"
                                    onClick={(e) => { e.stopPropagation(); openDeleteModal(u, 'user'); }}
                                    data-testid={`delete-user-${u.user_id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                                {u.is_disabled && (
                                  <Badge className="bg-red-500/20 text-red-400 text-[10px]">Disabled</Badge>
                                )}
                                {u.account_status === 'archived' && (
                                  <Badge className="bg-slate-500/20 text-slate-400 text-[10px]">Archived</Badge>
                                )}
                              </div>
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {/* PART D: pagination controls */}
              {totalFiltered > USERS_PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage <= 1}
                    onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                    data-testid="users-prev-btn"
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-40"
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-white/60" data-testid="users-page-indicator">
                    Page {safePage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage >= totalPages}
                    onClick={() => setUsersPage(p => Math.min(totalPages, p + 1))}
                    data-testid="users-next-btn"
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-40"
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
            );
          })()}
        </div>
      )}

      {/* P0: Registration Expiry Tab */}
      {activeTab === 'rego' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader
            title="Registration Expiry Management"
            subtitle="Monitor and manage vehicle registration compliance"
            actions={
              <Button 
                onClick={fetchData}
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10 w-full sm:w-auto h-10 text-sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            }
          />

          {loading ? <LoadingSpinner /> : (
            <>
              {/* Summary Stats */}
              <ResponsiveStatsGrid>
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    <Ban className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-red-400 mb-2 sm:mb-3 lg:mb-4" />
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold">
                      {expiringVehicles.filter(v => v.rego_status?.status === 'expired').length}
                    </p>
                    <p className="text-white/60 text-xs sm:text-sm">Expired (Hidden)</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-red-400 mb-2 sm:mb-3 lg:mb-4" />
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold">
                      {expiringVehicles.filter(v => v.rego_status?.warning_level === 'red').length}
                    </p>
                    <p className="text-white/60 text-xs sm:text-sm">Critical (≤7 days)</p>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-500/10 border-yellow-500/30">
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-yellow-400 mb-2 sm:mb-3 lg:mb-4" />
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold">
                      {expiringVehicles.filter(v => v.rego_status?.warning_level === 'yellow').length}
                    </p>
                    <p className="text-white/60 text-xs sm:text-sm">Warning (≤30 days)</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    <Car className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-purple-400 mb-2 sm:mb-3 lg:mb-4" />
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold">{expiringVehicles.length}</p>
                    <p className="text-white/60 text-xs sm:text-sm">Total Flagged</p>
                  </CardContent>
                </Card>
              </ResponsiveStatsGrid>

              {/* Expiring Vehicles Table */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
                    Vehicles With Registration Issues
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                  {expiringVehicles.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                      <p className="text-white/60 text-sm">All vehicles have valid registrations</p>
                    </div>
                  ) : (
                    <>
                      {/* Mobile Card View */}
                      <div className="lg:hidden space-y-3">
                        {expiringVehicles.map((vehicle) => (
                          <div 
                            key={vehicle.vehicle_id} 
                            className={`p-3 sm:p-4 rounded-xl ${
                              vehicle.rego_status?.status === 'expired' ? 'bg-red-500/10 border border-red-500/30' :
                              vehicle.rego_status?.warning_level === 'red' ? 'bg-red-500/5 border border-red-500/20' :
                              'bg-yellow-500/5 border border-yellow-500/20'
                            }`}
                            data-testid={`rego-vehicle-${vehicle.vehicle_id}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm truncate">{vehicle.make} {vehicle.model}</p>
                                <p className="text-xs text-white/60 truncate">{vehicle.registration} • {vehicle.rego_state}</p>
                              </div>
                              <Badge className={`text-[10px] ml-2 flex-shrink-0 ${
                                vehicle.rego_status?.status === 'expired' ? 'bg-red-500/20 text-red-400' :
                                vehicle.rego_status?.warning_level === 'red' ? 'bg-red-500/20 text-red-400' :
                                'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {vehicle.rego_status?.status === 'expired' ? 'EXPIRED' :
                                 vehicle.rego_status?.warning_level === 'red' ? 'CRITICAL' : 'WARNING'}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-white/60">
                                <span>Expiry: {vehicle.rego_expiry_date}</span>
                                {vehicle.rego_status?.days_until_expiry !== null && (
                                  <span className="ml-2">
                                    ({vehicle.rego_status.days_until_expiry < 0 
                                      ? `${Math.abs(vehicle.rego_status.days_until_expiry)} days ago` 
                                      : `${vehicle.rego_status.days_until_expiry} days left`})
                                  </span>
                                )}
                              </div>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-white/20 text-white h-7 text-xs"
                                onClick={() => {
                                  setSelectedVehicle(vehicle);
                                  setShowRegoOverrideModal(true);
                                }}
                              >
                                Override
                              </Button>
                            </div>
                            <p className="text-xs text-white/50 mt-2">Owner: {vehicle.owner_email || 'Unknown'}</p>
                          </div>
                        ))}
                      </div>

                      {/* Desktop Table View */}
                      <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Vehicle</th>
                              <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Registration</th>
                              <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Expiry Date</th>
                              <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Status</th>
                              <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Owner</th>
                              <th className="text-left py-3 px-4 font-medium text-sm text-white/60">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expiringVehicles.map((vehicle) => (
                              <tr 
                                key={vehicle.vehicle_id} 
                                className={`border-b border-white/5 ${
                                  vehicle.rego_status?.status === 'expired' ? 'bg-red-500/5' :
                                  vehicle.rego_status?.warning_level === 'red' ? 'bg-red-500/5' :
                                  'bg-yellow-500/5'
                                }`}
                                data-testid={`rego-row-${vehicle.vehicle_id}`}
                              >
                                <td className="py-3 px-4 text-sm">
                                  {vehicle.make} {vehicle.model} ({vehicle.year})
                                </td>
                                <td className="py-3 px-4 text-sm">
                                  {vehicle.registration}
                                  <span className="text-white/50 ml-1">({vehicle.rego_state})</span>
                                </td>
                                <td className="py-3 px-4 text-sm">
                                  {vehicle.rego_expiry_date}
                                  {vehicle.rego_status?.days_until_expiry !== null && (
                                    <span className={`text-xs ml-2 ${
                                      vehicle.rego_status.days_until_expiry < 0 ? 'text-red-400' :
                                      vehicle.rego_status.days_until_expiry <= 7 ? 'text-red-400' :
                                      'text-yellow-400'
                                    }`}>
                                      ({vehicle.rego_status.days_until_expiry < 0 
                                        ? `${Math.abs(vehicle.rego_status.days_until_expiry)} days ago` 
                                        : `${vehicle.rego_status.days_until_expiry} days`})
                                    </span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  <Badge className={`text-xs ${
                                    vehicle.rego_status?.status === 'expired' ? 'bg-red-500/20 text-red-400' :
                                    vehicle.rego_status?.warning_level === 'red' ? 'bg-red-500/20 text-red-400' :
                                    'bg-yellow-500/20 text-yellow-400'
                                  }`}>
                                    {vehicle.rego_status?.status === 'expired' ? 'EXPIRED - HIDDEN' :
                                     vehicle.rego_status?.warning_level === 'red' ? 'CRITICAL' : 'WARNING'}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4 text-sm text-white/60">
                                  {vehicle.owner_email || 'Unknown'}
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="border-white/20 text-white hover:bg-white/10 h-8 text-xs"
                                      onClick={() => {
                                        setSelectedVehicle(vehicle);
                                        setShowRegoOverrideModal(true);
                                      }}
                                    >
                                      Override
                                    </Button>
                                    {vehicle.rego_status?.status === 'expired' && (
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 text-xs"
                                        onClick={async () => {
                                          try {
                                            await api.post(`/platform-owner/vehicles/${vehicle.vehicle_id}/freeze`, {
                                              reason: 'rego_expired'
                                            });
                                            toast.success('Vehicle frozen');
                                            fetchData();
                                          } catch (err) {
                                            toast.error('Failed to freeze vehicle');
                                          }
                                        }}
                                      >
                                        Freeze
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Registration Enforcement Policy</p>
                      <ul className="text-xs sm:text-sm text-white/60 mt-2 space-y-1 list-disc list-inside">
                        <li><span className="text-red-400 font-medium">Expired:</span> Vehicle is automatically hidden from all public listings and cannot accept bookings</li>
                        <li><span className="text-red-400 font-medium">≤7 days (Critical):</span> Red warning shown to owner, vehicle remains visible but may need manual action</li>
                        <li><span className="text-yellow-400 font-medium">≤30 days (Warning):</span> Yellow warning shown to owner as a reminder to renew</li>
                        <li>All automated enforcement actions are logged for audit purposes</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* P0: Disputes Tab */}
      {activeTab === 'disputes' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader
            title="Dispute Management"
            subtitle="Review and resolve booking disputes"
            actions={
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <select
                  value={disputeStatusFilter}
                  onChange={(e) => { setDisputeStatusFilter(e.target.value); }}
                  className="h-10 px-3 rounded-md bg-white/10 border-white/20 text-white text-sm"
                  data-testid="dispute-status-filter"
                >
                  <option value="">All Disputes</option>
                  <option value="open">Open</option>
                  <option value="under_review">Under Review</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                <Button 
                  onClick={fetchData}
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/10 h-10 text-sm"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            }
          />

          {loading ? <LoadingSpinner /> : (
            <>
              {/* Disputes List */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
                    Disputes ({disputes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                  {disputes.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                      <p className="text-white/60 text-sm">No disputes found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {disputes.map((dispute) => (
                        <div 
                          key={dispute.dispute_id} 
                          className={`p-4 rounded-xl border cursor-pointer hover:bg-white/5 transition-colors ${
                            dispute.status === 'open' ? 'bg-red-500/10 border-red-500/30' :
                            dispute.status === 'under_review' ? 'bg-yellow-500/10 border-yellow-500/30' :
                            dispute.status === 'resolved' ? 'bg-green-500/10 border-green-500/30' :
                            'bg-white/5 border-white/10'
                          }`}
                          onClick={() => { setSelectedDispute(dispute); setShowDisputeDetailModal(true); }}
                          data-testid={`dispute-card-${dispute.dispute_id}`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-sm text-white truncate">#{dispute.dispute_id?.slice(0, 12)}</p>
                                <Badge className={`text-xs ${
                                  dispute.status === 'open' ? 'bg-red-500/20 text-red-400' :
                                  dispute.status === 'under_review' ? 'bg-yellow-500/20 text-yellow-400' :
                                  dispute.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                                  'bg-slate-500/20 text-slate-400'
                                }`}>
                                  {dispute.status?.replace('_', ' ').toUpperCase()}
                                </Badge>
                                <Badge variant="outline" className="border-white/20 text-white/60 text-xs">
                                  {dispute.type || 'general'}
                                </Badge>
                              </div>
                              <p className="text-xs text-white/60 mt-1">{dispute.reason}</p>
                              <div className="flex flex-wrap gap-2 mt-2 text-xs text-white/50">
                                <span>Booking: #{dispute.booking_id?.slice(0, 8)}</span>
                                <span>•</span>
                                <span>Vehicle: {dispute.vehicle}</span>
                                {dispute.disputed_amount && (
                                  <>
                                    <span>•</span>
                                    <span className="text-orange-400">${dispute.disputed_amount}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-white/50">
                              {new Date(dispute.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="mt-2 pt-2 border-t border-white/10 flex justify-between text-xs text-white/50">
                            <span>By: {dispute.raised_by_user?.full_name || dispute.raised_by_user?.email}</span>
                            <span>Against: {dispute.against_user?.full_name || dispute.against_user?.email}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* P0: Evidence Viewer Tab */}
      {activeTab === 'evidence' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader
            title="Evidence Viewer"
            subtitle="View all uploaded files for a booking (checklists, charge evidence, dispute evidence)"
          />

          {/* Search Form */}
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex-1">
                  <Label className="text-white/70 text-sm">Booking ID</Label>
                  <Input
                    placeholder="Enter booking ID (e.g., book_abc123...)"
                    value={evidenceBookingId}
                    onChange={(e) => setEvidenceBookingId(e.target.value)}
                    className="bg-white/10 border-white/20 text-white mt-1"
                    data-testid="evidence-booking-input"
                  />
                </div>
                <Button
                  onClick={async () => {
                    if (!evidenceBookingId.trim()) {
                      toast.error('Please enter a booking ID');
                      return;
                    }
                    setEvidenceLoading(true);
                    setEvidenceData(null);
                    try {
                      const res = await api.get(`/admin/evidence/${evidenceBookingId.trim()}`);
                      setEvidenceData(res.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to fetch evidence');
                    } finally {
                      setEvidenceLoading(false);
                    }
                  }}
                  disabled={evidenceLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white h-10"
                  data-testid="evidence-search-btn"
                >
                  {evidenceLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Search
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Evidence Results */}
          {evidenceData && (
            <div className="space-y-6">
              {/* Booking Info */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-400" />
                    Booking: {evidenceData.booking_id}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-white/50">Status:</span>
                      <Badge className="ml-2 bg-blue-500/20 text-blue-400">{evidenceData.booking_status}</Badge>
                    </div>
                    <div>
                      <span className="text-white/50">Vehicle:</span>
                      <span className="text-white ml-2">
                        {evidenceData.vehicle?.make} {evidenceData.vehicle?.model}
                      </span>
                    </div>
                    <div>
                      <span className="text-white/50">Rego:</span>
                      <span className="text-white ml-2">{evidenceData.vehicle?.registration || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-white/50">Total Files:</span>
                      <span className="text-white ml-2 font-bold">{evidenceData.total_files}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pre-Hire Checklist Photos */}
              {evidenceData.checklist_photos?.prehire?.length > 0 && (
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      Pre-Hire Checklist ({evidenceData.checklist_photos.prehire.length} photos)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {evidenceData.checklist_photos.prehire.map((photo, idx) => (
                        <div key={photo.photo_id || idx} className="relative group">
                          <div className="aspect-square bg-white/10 rounded-lg overflow-hidden border border-white/20">
                            {photo.thumbnail_url ? (
                              <img
                                src={photo.thumbnail_url}
                                alt={photo.photo_type}
                                className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition"
                                onClick={() => setSelectedImage(photo)}
                                data-testid={`prehire-thumb-${idx}`}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white/30">
                                <Image className="w-8 h-8" />
                              </div>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-white/60 truncate">{photo.photo_type}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Post-Hire Checklist Photos */}
              {evidenceData.checklist_photos?.posthire?.length > 0 && (
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400" />
                      Post-Hire Checklist ({evidenceData.checklist_photos.posthire.length} photos)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {evidenceData.checklist_photos.posthire.map((photo, idx) => (
                        <div key={photo.photo_id || idx} className="relative group">
                          <div className="aspect-square bg-white/10 rounded-lg overflow-hidden border border-white/20">
                            {photo.thumbnail_url ? (
                              <img
                                src={photo.thumbnail_url}
                                alt={photo.photo_type}
                                className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition"
                                onClick={() => setSelectedImage(photo)}
                                data-testid={`posthire-thumb-${idx}`}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white/30">
                                <Image className="w-8 h-8" />
                              </div>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-white/60 truncate">{photo.photo_type}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Charge Evidence */}
              {(evidenceData.evidence_files?.charge?.length > 0 || evidenceData.evidence_files?.resubmit?.length > 0) && (
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-amber-400" />
                      Charge Evidence ({(evidenceData.evidence_files?.charge?.length || 0) + (evidenceData.evidence_files?.resubmit?.length || 0)} files)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {[...(evidenceData.evidence_files?.charge || []), ...(evidenceData.evidence_files?.resubmit || [])].map((file, idx) => (
                        <div key={file.file_id || idx} className="relative group">
                          <div className="aspect-square bg-white/10 rounded-lg overflow-hidden border border-white/20">
                            {file.thumbnail_url ? (
                              <img
                                src={file.thumbnail_url}
                                alt={file.evidence_type}
                                className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition"
                                onClick={() => setSelectedImage(file)}
                                data-testid={`charge-evidence-thumb-${idx}`}
                              />
                            ) : file.content_type === 'application/pdf' ? (
                              <div className="w-full h-full flex items-center justify-center text-red-400 cursor-pointer" onClick={() => window.open(file.url, '_blank')}>
                                <FileText className="w-8 h-8" />
                              </div>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white/30">
                                <Image className="w-8 h-8" />
                              </div>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-white/60 truncate">
                            {file.evidence_type}
                            {file.evidence_type === 'resubmit' && <Badge className="ml-1 text-xs bg-amber-500/20 text-amber-400">Resubmit</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Dispute Evidence */}
              {evidenceData.evidence_files?.dispute?.length > 0 && (
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      Dispute Evidence ({evidenceData.evidence_files.dispute.length} files)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {evidenceData.evidence_files.dispute.map((file, idx) => (
                        <div key={file.file_id || idx} className="relative group">
                          <div className="aspect-square bg-white/10 rounded-lg overflow-hidden border border-white/20">
                            {file.thumbnail_url ? (
                              <img
                                src={file.thumbnail_url}
                                alt="dispute evidence"
                                className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition"
                                onClick={() => setSelectedImage(file)}
                                data-testid={`dispute-evidence-thumb-${idx}`}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white/30">
                                <Image className="w-8 h-8" />
                              </div>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-white/60 truncate">
                            {file.dispute_id ? `Dispute: ${file.dispute_id.slice(0, 8)}...` : 'dispute'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Empty state */}
              {evidenceData.total_files === 0 && (
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-8 text-center">
                    <Image className="w-12 h-12 mx-auto text-white/30 mb-4" />
                    <p className="text-white/60">No uploaded files found for this booking.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Image Lightbox Modal */}
          {selectedImage && (
            <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
              <DialogContent className="max-w-4xl bg-slate-900 border-white/20">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center justify-between">
                    <span>{selectedImage.photo_type || selectedImage.evidence_type || 'Image'}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(selectedImage.url, '_blank')}
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="open-full-image-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Full Image
                    </Button>
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-4">
                  <img
                    src={selectedImage.url}
                    alt="Full size"
                    className="max-w-full max-h-[70vh] mx-auto rounded-lg"
                    data-testid="full-size-image"
                  />
                  <div className="mt-4 text-sm text-white/60 grid grid-cols-2 gap-2">
                    <div>Uploaded: {new Date(selectedImage.uploaded_at).toLocaleString()}</div>
                    <div>By: {selectedImage.uploaded_by?.slice(0, 12)}...</div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}

      {/* P0: Overdue Trips Tab */}
      {activeTab === 'overdue' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader
            title="Overdue Trips"
            subtitle="Monitor and manage overdue vehicle returns"
            actions={
              <Button 
                onClick={fetchData}
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10 h-10 text-sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            }
          />

          {loading || overdueLoading ? <LoadingSpinner /> : (
            <>
              {/* Summary Stats */}
              <ResponsiveStatsGrid>
                <Card className="bg-yellow-500/10 border-yellow-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-yellow-400 font-medium">GRACE PERIOD</p>
                        <p className="text-2xl font-bold text-white">
                          {overdueTrips.filter(t => t.overdue_status === 'grace').length}
                        </p>
                      </div>
                      <Clock className="w-8 h-8 text-yellow-400 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-red-400 font-medium">OVERDUE</p>
                        <p className="text-2xl font-bold text-white">
                          {overdueTrips.filter(t => t.overdue_status === 'overdue').length}
                        </p>
                      </div>
                      <AlertTriangle className="w-8 h-8 text-red-400 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </ResponsiveStatsGrid>

              {/* Overdue Trips List */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
                      Overdue & Grace Trips ({overdueTrips.length})
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-500/20 text-green-400 text-xs">
                        LIVE DATA
                      </Badge>
                      {overdueDataMeta.test_data_excluded > 0 && (
                        <Badge className="bg-slate-500/20 text-slate-400 text-xs">
                          {overdueDataMeta.test_data_excluded} test excluded
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                  {overdueTrips.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                      <p className="text-white/60 text-sm">No overdue trips</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {overdueTrips.map((trip) => (
                        <div 
                          key={trip.booking_id} 
                          className={`p-4 rounded-xl border ${
                            trip.overdue_status === 'overdue' ? 'bg-red-500/10 border-red-500/30' :
                            trip.overdue_status === 'grace' ? 'bg-yellow-500/10 border-yellow-500/30' :
                            'bg-white/5 border-white/10'
                          }`}
                          data-testid={`overdue-trip-${trip.booking_id}`}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <Badge className={`text-xs ${
                                  trip.overdue_status === 'overdue' ? 'bg-red-500/20 text-red-400' :
                                  trip.overdue_status === 'grace' ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-slate-500/20 text-slate-400'
                                }`}>
                                  {trip.overdue_status?.toUpperCase()}
                                </Badge>
                                {trip.minutes_overdue > 0 && (
                                  <span className="text-xs text-red-400 font-medium">
                                    {trip.minutes_overdue} min overdue
                                  </span>
                                )}
                              </div>
                              <p className="font-medium text-white text-sm">
                                {trip.vehicle?.make} {trip.vehicle?.model} ({trip.vehicle?.rego_plate})
                              </p>
                              <p className="text-xs text-white/60 mt-1">
                                Booking: {trip.booking_id?.slice(0, 12)}...
                              </p>
                              <div className="grid grid-cols-2 gap-4 mt-2 text-xs text-white/60">
                                <div>
                                  <span className="text-white/40">Owner:</span> {trip.owner?.full_name || 'N/A'}
                                  <br/>
                                  <span className="text-white/40">Email:</span> {trip.owner?.email || 'N/A'}
                                </div>
                                <div>
                                  <span className="text-white/40">Renter:</span> {trip.renter?.full_name || 'N/A'}
                                  <br/>
                                  <span className="text-white/40">Email:</span> {trip.renter?.email || 'N/A'}
                                </div>
                              </div>
                              <div className="text-xs text-white/50 mt-2">
                                Due: {trip.trip_end_time ? new Date(trip.trip_end_time).toLocaleString() : 'N/A'} |
                                Grace ends: {trip.grace_end_time ? new Date(trip.grace_end_time).toLocaleString() : 'N/A'}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setSelectedOverdueBooking(trip); setShowAddNoteModal(true); }}
                                className="border-white/20 text-white hover:bg-white/10 text-xs h-8"
                              >
                                Add Note
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleExportIncidentPack(trip.booking_id)}
                                className="border-white/20 text-white hover:bg-white/10 text-xs h-8"
                              >
                                <Download className="w-3 h-3 mr-1" />
                                Export
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => { setSelectedOverdueBooking(trip); setShowResolveOverdueModal(true); }}
                                className="bg-green-600 hover:bg-green-700 text-xs h-8"
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Resolve
                              </Button>
                            </div>
                          </div>
                          {/* Notes */}
                          {trip.overdue_notes && trip.overdue_notes.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-white/10">
                              <p className="text-xs text-white/40 mb-2">Notes:</p>
                              {trip.overdue_notes.slice(-3).map((note, i) => (
                                <div key={i} className="text-xs text-white/60 mb-1">
                                  • {note.note} <span className="text-white/30">({new Date(note.added_at).toLocaleDateString()})</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Resolve Overdue Modal */}
      <Dialog open={showResolveOverdueModal} onOpenChange={setShowResolveOverdueModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Resolve Overdue Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-white/70">
              Booking: {selectedOverdueBooking?.booking_id?.slice(0, 16)}
            </p>
            <div>
              <Label>Resolution Reason (required)</Label>
              <Textarea
                value={overdueResolveReason}
                onChange={(e) => setOverdueResolveReason(e.target.value)}
                placeholder="e.g., Vehicle returned and verified, renter contacted support..."
                className="bg-white/10 border-white/20 text-white mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveOverdueModal(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleResolveOverdue} className="bg-green-600 hover:bg-green-700">
              Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog open={showAddNoteModal} onOpenChange={setShowAddNoteModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Add Overdue Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-white/70">
              Booking: {selectedOverdueBooking?.booking_id?.slice(0, 16)}
            </p>
            <div>
              <Label>Note</Label>
              <Textarea
                value={overdueNote}
                onChange={(e) => setOverdueNote(e.target.value)}
                placeholder="Add a note about this overdue booking..."
                className="bg-white/10 border-white/20 text-white mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNoteModal(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleAddOverdueNote} className="bg-blue-600 hover:bg-blue-700">
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader title="Platform Settings" />

          {isHostDirect && (
            <div
              className="p-3 rounded-lg border border-amber-400/40 bg-amber-500/10 text-amber-200 text-sm flex items-start gap-2"
              data-testid="platform-fees-inactive-note"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Platform fees are inactive in direct payment mode</p>
                <p className="text-amber-200/80 text-xs mt-0.5">
                  Fee values below remain stored for when platform payments are reactivated. They
                  are not charged on any booking while <code className="px-1 py-0.5 rounded bg-white/10">PAYMENT_MODE=host_direct</code>.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg">
                  <Percent className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                  Commission & Fees
                </CardTitle>
                <CardDescription className="text-white/60 text-xs sm:text-sm">
                  {isHostDirect
                    ? 'Stored fee values — not charged while direct-payment mode is active'
                    : 'Configure platform fees charged on each booking'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
                <div>
                  <Label className="text-white/80 text-sm">Platform Fee (%)</Label>
                  <div className="flex gap-2 sm:gap-3 mt-2">
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      step="0.5"
                      value={newCommission}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setNewCommission(isNaN(v) ? 0 : v);
                      }}
                      className="flex-1 bg-white/10 border-white/20 h-10 sm:h-11"
                      data-testid="settings-commission-input"
                    />
                    <span className="flex items-center text-white/60 text-sm">%</span>
                  </div>
                  {isHostDirect && (
                    <p
                      className="mt-2 text-xs text-amber-300/90"
                      data-testid="commission-hostdirect-hint-settings"
                    >
                      {newCommission === 0
                        ? '0% platform fee — inactive in direct payment mode.'
                        : 'Platform fees are currently inactive in direct payment mode. You can set this to 0% or configure it for future platform payment use.'}
                    </p>
                  )}
                </div>
                <Button 
                  onClick={handleSaveCommission}
                  disabled={savingCommission}
                  className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 h-10 sm:h-11 text-sm"
                >
                  {savingCommission ? 'Saving...' : 'Save Changes'}
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                  System Status
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {systemStatus ? (
                  <div className="space-y-2 sm:space-y-3">
                    {Object.entries(systemStatus).map(([key, sys]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-white/5 rounded-lg" data-testid={`system-status-${key}`}>
                        <div className="min-w-0 flex-1 mr-3">
                          <span className="text-xs sm:text-sm block">{sys.label}</span>
                          <span className="text-[10px] sm:text-xs text-white/40 block truncate">{sys.detail}</span>
                        </div>
                        <Badge className={sys.configured
                          ? "bg-green-500/20 text-green-400 text-xs shrink-0"
                          : "bg-amber-500/20 text-amber-400 text-xs shrink-0"
                        }>
                          {sys.configured ? 'Active' : 'Not Configured'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-white/5 rounded-lg animate-pulse" />)}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Platform Ownership Section */}
          <Card className="bg-white/5 border-white/10" data-testid="platform-ownership-section">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base lg:text-lg">
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
                Platform Ownership
              </CardTitle>
              <CardDescription className="text-white/60 text-xs sm:text-sm">
                Current platform owner and transfer controls
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                <div className="w-9 h-9 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                  {(user?.full_name || 'O')[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{user?.full_name || 'Platform Owner'}</p>
                  <p className="text-xs text-white/50 truncate">{user?.email}</p>
                </div>
                <Badge className="bg-purple-500/20 text-purple-300 text-xs shrink-0">Owner</Badge>
              </div>
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 text-sm h-10"
                onClick={() => navigate('/platform-owner/transfer-ownership')}
                data-testid="transfer-ownership-settings-btn"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Transfer Ownership
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment Dashboard Tab */}
      {activeTab === 'stripe' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader title="Payment Dashboard" />
          
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6 text-center">
              <CreditCard className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Access Payment Dashboard</h3>
              <p className="text-white/60 text-sm mb-4">
                Manage your Airwallex account, view detailed reports, and configure payouts.
              </p>
              <Button 
                onClick={() => window.open('https://www.airwallex.com/app', '_blank', 'noopener,noreferrer')}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="open-payment-dashboard-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Airwallex Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Admins Tab */}
      {activeTab === 'admins' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader
            title="Admin Management"
            actions={
              <Button 
                onClick={() => setShowGrantModal(true)} 
                className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto h-10 sm:h-11 text-sm"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Grant Role
              </Button>
            }
          />

          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-0">
              {loading ? <LoadingSpinner /> : admins.length === 0 ? (
                <p className="text-center py-8 sm:py-12 text-white/60 text-sm">No admins found</p>
              ) : (
                <div className="divide-y divide-white/10">
                  {admins.map((admin) => (
                    <div key={admin.user_id} className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm ${
                          admin.is_platform_owner ? 'bg-gradient-to-br from-yellow-400 to-orange-500' :
                          admin.highest_role === 'super_admin' ? 'bg-purple-600' : 'bg-blue-600'
                        }`}>
                          {admin.is_platform_owner ? <Crown className="w-4 h-4 sm:w-5 sm:h-5" /> : admin.full_name?.charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm sm:text-base truncate">{admin.full_name}</p>
                          <p className="text-xs sm:text-sm text-white/60 truncate">{admin.email}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 pl-12 sm:pl-0">
                        <div className="flex gap-1 flex-wrap">
                          {admin.roles?.filter(r => r.is_active).map((r, i) => (
                            <Badge 
                              key={i} 
                              className={`text-[10px] sm:text-xs ${
                                r.role === 'platform_owner' ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0' :
                                r.role === 'super_admin' ? 'bg-purple-500/20 text-purple-300 border-purple-500/50' :
                                'bg-blue-500/20 text-blue-300 border-blue-500/50'
                              }`}
                            >
                              {r.role.replace('_', ' ')}
                            </Badge>
                          ))}
                        </div>
                        {admin.is_protected ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-[10px] sm:text-xs">Protected</Badge>
                        ) : (
                          <div className="flex gap-2">
                            {admin.highest_role === 'super_admin' && (
                              <Button 
                                size="sm" 
                                variant="destructive"
                                className="text-xs h-8"
                                onClick={() => handleRevokeRole(admin.user_id, 'super_admin')}
                              >
                                Revoke
                              </Button>
                            )}
                            {admin.highest_role === 'admin' && (
                              <Button 
                                size="sm" 
                                variant="destructive"
                                className="text-xs h-8"
                                onClick={() => handleRevokeRole(admin.user_id, 'admin')}
                              >
                                Revoke
                              </Button>
                            )}
                            {/* Note: Admin/Super Admin accounts are protected - no delete button */}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Audit Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader
            title="Audit Logs"
            actions={
              <Button 
                onClick={handleExportAuditLogs} 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10 w-full sm:w-auto h-10 sm:h-11 text-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Export All
              </Button>
            }
          />

          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-0">
              {loading ? <LoadingSpinner /> : auditLogs.length === 0 ? (
                <p className="text-center py-8 sm:py-12 text-white/60 text-sm">No audit logs</p>
              ) : (
                <div className="max-h-[500px] sm:max-h-[600px] overflow-y-auto divide-y divide-white/10">
                  {auditLogs.map((log) => (
                    <div key={log.log_id} className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                        <Badge variant="outline" className="border-white/30 self-start text-xs">{log.action}</Badge>
                        <span className="text-[10px] sm:text-xs text-white/50">
                          {new Date(log.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-white/80">
                        {log.entity_type}: {log.entity_id?.slice(0, 16)}
                      </p>
                      <p className="text-[10px] sm:text-xs text-white/50 mt-1">By: {log.user_email}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Legal Pages Tab - NEW IMPLEMENTATION */}
      {activeTab === 'legal' && !showLegalEditorModal && (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <ResponsivePageHeader
            title="Legal Pages"
            subtitle="Edit terms, privacy policy, and insurance information with draft/publish workflow"
            actions={
              <Button 
                onClick={fetchData}
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10 h-10 text-sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            }
          />

          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {[
                  { key: 'terms', title: 'Terms of Service', desc: 'User agreement and platform rules', icon: FileText },
                  { key: 'privacy', title: 'Privacy Policy', desc: 'Data collection and usage policies', icon: Shield },
                  { key: 'insurance', title: 'Insurance Info', desc: 'Insurance coverage and disclaimers', icon: Shield },
                  { key: 'community-guidelines', title: 'Community Guidelines', desc: 'Standards of behaviour on the platform', icon: Gavel },
                  { key: 'trust-safety', title: 'Trust & Safety', desc: 'How HireHub protects renters and hosts', icon: Shield },
                  { key: 'renter-agreement', title: 'Renter Agreement', desc: 'Renter responsibilities and rules', icon: FileText }
                ].map((page) => {
                  const status = legalPagesStatus.find(p => p.type === page.key) || {};
                  return (
                    <Card 
                      key={page.key}
                      className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors cursor-pointer h-full"
                      onClick={() => {
                        setSelectedLegalPage(page.key);
                        setShowLegalEditorModal(true);
                      }}
                      data-testid={`legal-card-${page.key}`}
                    >
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-start justify-between mb-3">
                          <page.icon className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />
                          <div className="flex items-center gap-2">
                            {status.is_dirty && (
                              <Badge className="bg-amber-500/20 text-amber-400 text-[10px]" data-testid={`dirty-badge-${page.key}`}>
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Draft
                              </Badge>
                            )}
                            {status.has_published && (
                              <Badge className="bg-green-500/20 text-green-400 text-[10px]" data-testid={`live-badge-${page.key}`}>
                                {status.version_id}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="font-medium text-sm sm:text-base">{page.title}</p>
                        <p className="text-xs sm:text-sm text-white/60 mt-1">{page.desc}</p>
                        
                        {status.published_at && (
                          <p className="text-xs text-white/40 mt-3 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Published: {new Date(status.published_at).toLocaleDateString()}
                          </p>
                        )}
                        
                        <Button 
                          variant="outline"
                          size="sm"
                          className="w-full mt-4 border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Content
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Draft → Publish Workflow</p>
                      <ul className="text-xs sm:text-sm text-white/60 mt-2 space-y-1 list-disc list-inside">
                        <li><strong>Auto-Save:</strong> Edits are automatically saved as drafts (not visible to users)</li>
                        <li><strong>Preview:</strong> Review your changes before publishing</li>
                        <li><strong>Publish:</strong> Click "Publish to Live" to make changes visible</li>
                        <li><strong>Version History:</strong> Previous versions are archived and can be restored</li>
                        <li><strong>Booking Binding:</strong> Each booking records which legal version was accepted</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-purple-500/10 border-purple-500/30">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start gap-3">
                    <Eye className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Public Pages</p>
                      <p className="text-xs sm:text-sm text-white/60 mt-1">
                        View the live public pages as users see them:
                      </p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <a href="/legal/terms" target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs">
                            <ExternalLink className="w-3 h-3 mr-1" /> Terms
                          </Button>
                        </a>
                        <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs">
                            <ExternalLink className="w-3 h-3 mr-1" /> Privacy
                          </Button>
                        </a>
                        <a href="/legal/insurance" target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs">
                            <ExternalLink className="w-3 h-3 mr-1" /> Insurance
                          </Button>
                        </a>
                        <a href="/legal/community-guidelines" target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs">
                            <ExternalLink className="w-3 h-3 mr-1" /> Community
                          </Button>
                        </a>
                        <a href="/trust-safety" target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs">
                            <ExternalLink className="w-3 h-3 mr-1" /> Trust & Safety
                          </Button>
                        </a>
                        <a href="/renter-agreement" target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs">
                            <ExternalLink className="w-3 h-3 mr-1" /> Renter Agreement
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Legal Editor Modal (Full Screen) */}
      {activeTab === 'legal' && showLegalEditorModal && selectedLegalPage && (
        <LegalEditor
          pageType={selectedLegalPage}
          onClose={() => {
            setShowLegalEditorModal(false);
            setSelectedLegalPage(null);
            fetchData(); // Refresh status after editing
          }}
        />
      )}

      {/* Upgrade Requests Tab */}
      {activeTab === 'upgrade-requests' && (
        <div className="space-y-4 sm:space-y-6">
          <ResponsivePageHeader
            title="Upgrade Requests"
            subtitle="Review host plan upgrade interest"
            badge={
              (upgradeRequestsCounts.pending || 0) > 0 ? (
                <Badge className="bg-amber-500 text-white border-0 text-xs">
                  {upgradeRequestsCounts.pending} pending
                </Badge>
              ) : null
            }
          />
          
          {/* Status Filter Tabs */}
          <div className="flex flex-wrap gap-2" data-testid="upgrade-requests-filters">
            {[
              { id: 'all', label: 'All' },
              { id: 'pending', label: 'Pending' },
              { id: 'reviewing', label: 'Reviewing' },
              { id: 'approved', label: 'Approved' },
              { id: 'declined', label: 'Declined' },
              { id: 'completed', label: 'Completed' },
            ].map(f => (
              <Button
                key={f.id}
                variant={upgradeRequestsFilter === f.id ? 'default' : 'outline'}
                size="sm"
                className={`h-8 text-xs ${upgradeRequestsFilter === f.id ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                onClick={() => {
                  setUpgradeRequestsFilter(f.id);
                  // Trigger refetch
                  setUpgradeRequestsLoading(true);
                  const url = f.id !== 'all' ? `/admin/upgrade-requests?status=${f.id}` : '/admin/upgrade-requests';
                  api.get(url).then(res => {
                    setUpgradeRequests(res.data.requests || []);
                    setUpgradeRequestsCounts(res.data.counts || {});
                    setUpgradeRequestsLoading(false);
                  }).catch(() => setUpgradeRequestsLoading(false));
                }}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
                {f.id !== 'all' && upgradeRequestsCounts[f.id] > 0 && (
                  <span className="ml-1.5 bg-white/20 px-1.5 rounded-full text-[10px]">
                    {upgradeRequestsCounts[f.id]}
                  </span>
                )}
              </Button>
            ))}
          </div>

          {upgradeRequestsFilter === 'completed' && (
            <p className="text-xs text-slate-500 italic" data-testid="completed-filter-helper">
              Completed = the host's tier change has been applied. Set this manually after applying the tier upgrade.
            </p>
          )}
          {(upgradeRequestsFilter === 'approved' || upgradeRequestsFilter === 'declined' || upgradeRequestsFilter === 'reviewing') && (
            <p className="text-xs text-slate-500 italic" data-testid="status-filter-helper">
              Sorted by most recent {upgradeRequestsFilter === 'reviewing' ? 'review activity' : `${upgradeRequestsFilter} action`} first.
            </p>
          )}

          {upgradeRequestsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : upgradeRequests.length === 0 ? (
            <Card className="border-dashed border-slate-300">
              <CardContent className="p-8 text-center">
                <Zap className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No upgrade requests</p>
                <p className="text-xs text-slate-400 mt-1">
                  {upgradeRequestsFilter !== 'all' ? `No ${upgradeRequestsFilter} requests found. Try another filter.` : 'Requests from hosts will appear here when they express upgrade interest.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="upgrade-requests-list">
              {upgradeRequests.map((req) => {
                const statusColors = {
                  pending: 'bg-amber-100 text-amber-700 border-amber-200',
                  reviewing: 'bg-blue-100 text-blue-700 border-blue-200',
                  approved: 'bg-green-100 text-green-700 border-green-200',
                  declined: 'bg-red-100 text-red-700 border-red-200',
                  completed: 'bg-slate-100 text-slate-600 border-slate-200',
                };
                const tierColors = {
                  free: 'bg-slate-100 text-slate-600',
                  basic: 'bg-blue-100 text-blue-700',
                  pro: 'bg-violet-100 text-violet-700',
                  premium: 'bg-amber-100 text-amber-700',
                };
                return (
                  <Card key={req.request_id} className="border-slate-200 hover:shadow-sm transition-shadow" data-testid={`upgrade-request-${req.request_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        {/* Host Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-slate-900 truncate">{req.host_name || req.full_name || 'Unknown'}</span>
                            <Badge className={`text-[10px] px-1.5 py-0 h-4 border ${statusColors[req.status] || statusColors.pending}`}>
                              {req.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500 truncate">{req.host_email || req.email}</p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className="text-xs text-slate-400">Current:</span>
                            <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${tierColors[req.live_tier || req.current_tier] || tierColors.free}`}>
                              {(req.live_tier || req.current_tier || 'free').charAt(0).toUpperCase() + (req.live_tier || req.current_tier || 'free').slice(1)}
                            </span>
                            <ChevronRight className="w-3 h-3 text-slate-300" />
                            <span className="text-xs text-slate-400">Requested:</span>
                            <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${tierColors[req.requested_tier] || tierColors.pro}`}>
                              {req.requested_tier.charAt(0).toUpperCase() + req.requested_tier.slice(1)}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-400 mt-1" data-testid={`upgrade-request-requested-at-${req.request_id}`}>
                            Requested: {req.created_at ? new Date(req.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </p>
                          {(() => {
                            const statusTsMap = {
                              approved: { ts: req.approved_at, label: 'Approved', actor: req.approved_by, testid: 'upgrade-request-approved-at' },
                              declined: { ts: req.declined_at, label: 'Declined', actor: req.declined_by, testid: 'upgrade-request-declined-at' },
                              reviewing: { ts: req.reviewing_at, label: 'Reviewing since', actor: req.reviewing_by, testid: 'upgrade-request-reviewing-at' },
                              completed: { ts: req.completed_at, label: 'Completed', actor: req.completed_by, testid: 'upgrade-request-completed-at' },
                            };
                            const meta = statusTsMap[req.status];
                            if (!meta || !meta.ts) return null;
                            const labelColors = {
                              Approved: 'text-green-700',
                              Declined: 'text-red-700',
                              'Reviewing since': 'text-blue-700',
                              Completed: 'text-slate-600',
                            };
                            return (
                              <p className={`text-[11px] mt-0.5 font-medium ${labelColors[meta.label] || 'text-slate-500'}`} data-testid={`${meta.testid}-${req.request_id}`}>
                                {meta.label}: {new Date(meta.ts).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                {meta.actor ? ` · by ${meta.actor}` : ''}
                              </p>
                            );
                          })()}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                          {/* Status quick-actions */}
                          {req.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                              onClick={async () => {
                                try {
                                  await api.patch(`/admin/upgrade-requests/${req.request_id}`, { status: 'reviewing' });
                                  toast.success('Marked as reviewing');
                                  fetchData();
                                } catch { toast.error('Failed to update'); }
                              }}
                              data-testid={`mark-reviewing-${req.request_id}`}
                            >
                              Review
                            </Button>
                          )}
                          {(req.status === 'pending' || req.status === 'reviewing') && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs border-green-200 text-green-700 hover:bg-green-50"
                                onClick={async () => {
                                  try {
                                    await api.patch(`/admin/upgrade-requests/${req.request_id}`, { status: 'approved' });
                                    toast.success('Approved');
                                    fetchData();
                                  } catch { toast.error('Failed to update'); }
                                }}
                                data-testid={`approve-${req.request_id}`}
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50"
                                onClick={async () => {
                                  try {
                                    await api.patch(`/admin/upgrade-requests/${req.request_id}`, { status: 'declined' });
                                    toast.success('Declined');
                                    fetchData();
                                  } catch { toast.error('Failed to update'); }
                                }}
                                data-testid={`decline-${req.request_id}`}
                              >
                                <X className="w-3 h-3 mr-1" />
                                Decline
                              </Button>
                            </>
                          )}
                          {/* Review Host (opens UserDetailsDrawer) */}
                          <Button
                            size="sm"
                            className="h-8 text-xs bg-purple-600 hover:bg-purple-700"
                            onClick={async () => {
                              try {
                                const res = await api.get(`/admin/users/${req.user_id}`);
                                setSelectedUser(res.data);
                                setShowUserModal(true);
                              } catch { toast.error('Could not load host record'); }
                            }}
                            data-testid={`review-host-${req.request_id}`}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Review Host
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}


      {/* Ranking Controls Tab */}
      {activeTab === 'ranking-controls' && (
        <div className="space-y-4 sm:space-y-6">
          <ResponsivePageHeader
            title="Ranking Controls"
            subtitle="Configure listing ranking weights and boost multipliers"
            badge={rankingConfig?.is_default ? (
              <Badge variant="outline" className="text-xs text-slate-400 border-slate-200">Using Defaults</Badge>
            ) : (
              <Badge className="bg-green-100 text-green-700 border-green-200 text-xs border">Custom Config Active</Badge>
            )}
          />

          {rankingConfigLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : rankingConfig ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Boost Multipliers */}
              <Card className="border-slate-200" data-testid="ranking-boost-multipliers">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-violet-500" />
                    Boost Multipliers
                  </CardTitle>
                  <p className="text-xs text-slate-500">Applied after base score. Higher tiers get more visibility. Must be at least 1.0.</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {['free', 'basic', 'pro', 'premium'].map(tier => {
                    const tierColors = { free: 'text-slate-600', basic: 'text-blue-600', pro: 'text-violet-600', premium: 'text-amber-600' };
                    return (
                      <div key={tier} className="flex items-center justify-between gap-4">
                        <label className={`text-sm font-medium capitalize ${tierColors[tier]}`}>{tier}</label>
                        <input
                          type="number"
                          min="1.0"
                          step="0.05"
                          value={rankingConfig.boost_multipliers?.[tier] ?? 1.0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val)) {
                              setRankingConfig(prev => ({
                                ...prev,
                                boost_multipliers: { ...prev.boost_multipliers, [tier]: val }
                              }));
                            }
                          }}
                          className="w-24 h-9 px-3 text-sm border border-slate-200 rounded-lg text-right focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          data-testid={`multiplier-${tier}`}
                        />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Scoring Weights */}
              <Card className="border-slate-200" data-testid="ranking-scoring-weights">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Sliders className="w-4 h-4 text-blue-500" />
                    Scoring Weights
                  </CardTitle>
                  <p className="text-xs text-slate-500">
                    Base score components (0-100 scale). Total: {Object.values(rankingConfig.weights || {}).reduce((a, b) => a + b, 0)}
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { key: 'availability', label: 'Availability', hint: 'Available for requested dates' },
                    { key: 'photos', label: 'Photos', hint: 'Has photos (scaled by count)' },
                    { key: 'description', label: 'Description', hint: 'Has a description' },
                    { key: 'pricing', label: 'Pricing', hint: 'Competitively priced' },
                    { key: 'instant_book', label: 'Instant Book', hint: 'Instant booking enabled' },
                    { key: 'bond', label: 'Bond', hint: 'Reasonable bond amount' },
                    { key: 'freshness', label: 'Freshness', hint: 'Recently listed' },
                  ].map(w => (
                    <div key={w.key} className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <label className="text-sm font-medium text-slate-700">{w.label}</label>
                        <p className="text-[11px] text-slate-400">{w.hint}</p>
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={rankingConfig.weights?.[w.key] ?? 0}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val)) {
                            setRankingConfig(prev => ({
                              ...prev,
                              weights: { ...prev.weights, [w.key]: val }
                            }));
                          }
                        }}
                        className="w-20 h-9 px-3 text-sm border border-slate-200 rounded-lg text-right focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        data-testid={`weight-${w.key}`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Advanced Settings */}
              <Card className="border-slate-200 lg:col-span-2" data-testid="ranking-advanced-settings">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-slate-800">Advanced</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Quality Floor</label>
                        <p className="text-[11px] text-slate-400">Min base score for boost to apply</p>
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="5"
                        value={rankingConfig.quality_floor ?? 25}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val)) setRankingConfig(prev => ({ ...prev, quality_floor: val }));
                        }}
                        className="w-20 h-9 px-3 text-sm border border-slate-200 rounded-lg text-right focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        data-testid="quality-floor-input"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Max Consecutive Same Host</label>
                        <p className="text-[11px] text-slate-400">Diversity cap for host results</p>
                      </div>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={rankingConfig.max_consecutive_same_host ?? 2}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (!isNaN(val)) setRankingConfig(prev => ({ ...prev, max_consecutive_same_host: val }));
                        }}
                        className="w-20 h-9 px-3 text-sm border border-slate-200 rounded-lg text-right focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        data-testid="max-consecutive-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="lg:col-span-2 flex justify-end">
                <Button
                  className="bg-purple-600 hover:bg-purple-700 h-10 px-6 text-sm"
                  onClick={async () => {
                    setRankingConfigSaving(true);
                    try {
                      const payload = {
                        boost_multipliers: rankingConfig.boost_multipliers,
                        weights: rankingConfig.weights,
                        quality_floor: rankingConfig.quality_floor,
                        max_consecutive_same_host: rankingConfig.max_consecutive_same_host,
                      };
                      const res = await api.patch('/admin/ranking-config', payload);
                      setRankingConfig({ ...res.data.config, is_default: false });
                      toast.success('Ranking config saved');
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Failed to save config');
                    }
                    setRankingConfigSaving(false);
                  }}
                  disabled={rankingConfigSaving}
                  data-testid="save-ranking-config"
                >
                  {rankingConfigSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Ranking Config
                </Button>
              </div>
            </div>
          ) : (
            <Card className="border-dashed border-slate-300">
              <CardContent className="p-8 text-center">
                <Sliders className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Could not load ranking config</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}


      {/* GPS Alerts Tab (PO Oversight) */}
      {activeTab === 'gps-alerts' && (
        <div className="space-y-4 sm:space-y-6">
          <ResponsivePageHeader
            title="GPS Alerts"
            subtitle="Monitor GPS alerts across all host vehicles"
            badge={(poGpsAlertsCounts.active || 0) > 0 ? (
              <Badge className="bg-red-500 text-white border-0 text-xs">{poGpsAlertsCounts.active} active</Badge>
            ) : null}
          />
          <div className="flex flex-wrap gap-2" data-testid="po-gps-alerts-filters">
            {[
              { id: 'active', label: 'Active' }, { id: 'acknowledged', label: 'Acknowledged' },
              { id: 'resolved', label: 'Resolved' }, { id: 'all', label: 'All' },
            ].map(f => (
              <Button key={f.id} variant={poGpsAlertsFilter === f.id ? 'default' : 'outline'} size="sm"
                className={`h-8 text-xs ${poGpsAlertsFilter === f.id ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                onClick={() => {
                  setPoGpsAlertsFilter(f.id);
                  setPoGpsAlertsLoading(true);
                  const url = f.id !== 'all' ? `/platform-owner/gps-alerts?status=${f.id}` : '/platform-owner/gps-alerts';
                  api.get(url).then(res => { setPoGpsAlerts(res.data.alerts || []); setPoGpsAlertsCounts(res.data.counts || {}); setPoGpsAlertsLoading(false); }).catch(() => setPoGpsAlertsLoading(false));
                }}
                data-testid={`po-gps-filter-${f.id}`}>{f.label}
                {f.id !== 'all' && poGpsAlertsCounts[f.id] > 0 && <span className="ml-1.5 opacity-70">({poGpsAlertsCounts[f.id]})</span>}
              </Button>
            ))}
          </div>
          {poGpsAlertsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>
          ) : poGpsAlerts.length === 0 ? (
            <Card className="border-dashed border-slate-300"><CardContent className="p-8 text-center">
              <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No GPS alerts</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="po-gps-alerts-list">
              {poGpsAlerts.map(alert => {
                const sevColors = { high: 'bg-red-100 text-red-700 border-red-200', medium: 'bg-amber-100 text-amber-700 border-amber-200', low: 'bg-slate-100 text-slate-600 border-slate-200' };
                const statColors = { active: 'bg-red-50 text-red-600 border-red-200', acknowledged: 'bg-blue-50 text-blue-600 border-blue-200', resolved: 'bg-green-50 text-green-600 border-green-200' };
                return (
                  <Card key={alert.alert_id} className="border-slate-200" data-testid={`po-gps-alert-${alert.alert_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900">{alert.vehicle_name || alert.vehicle_id}</span>
                            <Badge className={`text-[10px] px-1.5 py-0 h-4 border ${sevColors[alert.severity] || sevColors.medium}`}>{alert.severity}</Badge>
                            <Badge className={`text-[10px] px-1.5 py-0 h-4 border ${statColors[alert.status] || statColors.active}`}>{alert.status}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-0.5">{alert.message}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                            <span>Host: {alert.host_name || alert.host_id}</span>
                            <span>{alert.created_at ? new Date(alert.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Support Tickets Tab (PO Management) */}
      {activeTab === 'support-tickets' && (
        <div className="space-y-4 sm:space-y-6">
          <ResponsivePageHeader
            title="Support Tickets"
            subtitle="Manage host support requests"
            badge={(poSupportTicketsCounts.open || 0) > 0 ? (
              <Badge className="bg-amber-500 text-white border-0 text-xs">{poSupportTicketsCounts.open} open</Badge>
            ) : null}
          />
          <div className="flex flex-wrap gap-2" data-testid="po-support-filters">
            {[
              { id: 'open', label: 'Open' }, { id: 'in_progress', label: 'In Progress' },
              { id: 'resolved', label: 'Resolved' }, { id: 'all', label: 'All' },
            ].map(f => (
              <Button key={f.id} variant={poSupportTicketsFilter === f.id ? 'default' : 'outline'} size="sm"
                className={`h-8 text-xs ${poSupportTicketsFilter === f.id ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                onClick={() => {
                  setPoSupportTicketsFilter(f.id);
                  setPoSupportTicketsLoading(true);
                  const url = f.id !== 'all' ? `/admin/support-tickets?status=${f.id}` : '/admin/support-tickets';
                  api.get(url).then(res => { setPoSupportTickets(res.data.tickets || []); setPoSupportTicketsCounts(res.data.counts || {}); setPoSupportTicketsLoading(false); }).catch(() => setPoSupportTicketsLoading(false));
                }}
                data-testid={`po-support-filter-${f.id}`}>{f.label}
                {f.id !== 'all' && poSupportTicketsCounts[f.id] > 0 && <span className="ml-1.5 opacity-70">({poSupportTicketsCounts[f.id]})</span>}
              </Button>
            ))}
          </div>
          {poSupportTicketsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>
          ) : poSupportTickets.length === 0 ? (
            <Card className="border-dashed border-slate-300"><CardContent className="p-8 text-center">
              <HeadphonesIcon className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No support tickets</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="po-support-tickets-list">
              {poSupportTickets.map(ticket => {
                const statColors = { open: 'bg-blue-100 text-blue-700 border-blue-200', in_progress: 'bg-amber-100 text-amber-700 border-amber-200', resolved: 'bg-green-100 text-green-700 border-green-200' };
                const prioColors = { normal: 'bg-slate-100 text-slate-600 border-slate-200', high: 'bg-violet-100 text-violet-700 border-violet-200', urgent: 'bg-red-100 text-red-700 border-red-200' };
                return (
                  <Card key={ticket.ticket_id} className="border-slate-200" data-testid={`po-ticket-${ticket.ticket_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900">{ticket.subject}</span>
                            <Badge className={`text-[10px] px-1.5 py-0 h-4 border ${statColors[ticket.status] || statColors.open}`}>{ticket.status === 'in_progress' ? 'In Progress' : ticket.status}</Badge>
                            <Badge className={`text-[10px] px-1.5 py-0 h-4 border ${prioColors[ticket.priority] || prioColors.normal}`}>{ticket.priority}</Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{ticket.message}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                            <span>{ticket.host_name || ticket.host_email}</span>
                            <span>Tier: {ticket.tier}</span>
                            <span>{ticket.created_at ? new Date(ticket.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                          {ticket.status === 'open' && (
                            <Button size="sm" variant="outline" className="h-8 text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                              onClick={async () => { try { await api.patch(`/admin/support-tickets/${ticket.ticket_id}`, { status: 'in_progress' }); toast.success('Marked in progress'); fetchData(); } catch { toast.error('Failed'); } }}
                              data-testid={`po-ticket-progress-${ticket.ticket_id}`}>In Progress</Button>
                          )}
                          {(ticket.status === 'open' || ticket.status === 'in_progress') && (
                            <Button size="sm" variant="outline" className="h-8 text-xs border-green-200 text-green-700 hover:bg-green-50"
                              onClick={async () => { try { await api.patch(`/admin/support-tickets/${ticket.ticket_id}`, { status: 'resolved' }); toast.success('Resolved'); fetchData(); } catch { toast.error('Failed'); } }}
                              data-testid={`po-ticket-resolve-${ticket.ticket_id}`}><CheckCircle className="w-3 h-3 mr-1" />Resolve</Button>
                          )}
                          <Button size="sm" className="h-8 text-xs bg-purple-600 hover:bg-purple-700"
                            onClick={async () => { try { const res = await api.get(`/admin/users/${ticket.host_id}`); setSelectedUser(res.data); setShowUserModal(true); } catch { toast.error('Could not load host'); } }}
                            data-testid={`po-ticket-view-host-${ticket.ticket_id}`}><Eye className="w-3 h-3 mr-1" />View Host</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}


      {/* User Details Drawer - Enhanced Multi-Tab Panel */}
      <UserDetailsDrawer
        isOpen={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
          setSelectedVerification(null);
        }}
        onRefresh={() => {
          setShowUserModal(false);
          setSelectedUser(null);
          setSelectedVerification(null);
          fetchData();
        }}
        user={selectedUser}
        verification={selectedVerification}
        api={api}
        onApproveVerification={handleApproveVerification}
        onRejectVerification={() => setShowRejectModal(true)}
        onOpenDocument={openDocument}
        actionLoading={actionLoading}
        onStartShadowSession={handleStartShadowSession}
        onDeleteUser={openDeleteModal}
      />

      {/* Reject Verification Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="bg-slate-900 border-white/20 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Reject Verification</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-white/80 text-sm">Reason for Rejection</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason (will be sent to user)..."
              rows={3}
              className="mt-2 bg-white/10 border-white/20 text-white"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowRejectModal(false); setRejectReason(''); }} 
              className="border-white/20 w-full sm:w-auto h-10"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRejectVerification} 
              disabled={actionLoading}
              className="w-full sm:w-auto h-10"
              data-testid="confirm-reject-btn"
            >
              {actionLoading ? 'Rejecting...' : 'Reject Verification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Role Modal */}
      <Dialog open={showGrantModal} onOpenChange={setShowGrantModal}>
        <DialogContent className="bg-slate-900 border-white/20 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Grant Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/80 text-sm">Role Type</Label>
              <select
                value={grantType}
                onChange={(e) => setGrantType(e.target.value)}
                className="w-full mt-2 h-10 sm:h-11 rounded-md border border-white/20 bg-white/10 px-3 text-sm"
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div>
              <Label className="text-white/80 text-sm">User ID</Label>
              <Input
                value={grantUserId}
                onChange={(e) => setGrantUserId(e.target.value)}
                placeholder="Enter user_id..."
                className="mt-2 bg-white/10 border-white/20 h-10 sm:h-11"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowGrantModal(false)} 
              className="border-white/20 w-full sm:w-auto h-10 sm:h-11"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleGrantRole} 
              className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto h-10 sm:h-11"
            >
              Grant Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0: Rego Override Modal */}
      <Dialog open={showRegoOverrideModal} onOpenChange={setShowRegoOverrideModal}>
        <DialogContent className="bg-slate-900 border-white/20 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <Car className="w-5 h-5 text-orange-400" />
              Override Registration Status
            </DialogTitle>
          </DialogHeader>
          {selectedVehicle && (
            <div className="py-4 space-y-4">
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="font-medium text-sm">{selectedVehicle.make} {selectedVehicle.model}</p>
                <p className="text-xs text-white/60 mt-1">
                  {selectedVehicle.registration} ({selectedVehicle.rego_state}) • Expires: {selectedVehicle.rego_expiry_date}
                </p>
                <Badge className={`mt-2 text-xs ${
                  selectedVehicle.rego_status?.status === 'expired' ? 'bg-red-500/20 text-red-400' :
                  selectedVehicle.rego_status?.warning_level === 'red' ? 'bg-red-500/20 text-red-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  Current: {selectedVehicle.rego_status?.status?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              </div>
              
              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-xs text-orange-300">
                  <strong>Warning:</strong> Overriding registration status is for exceptional circumstances only 
                  (e.g., processing delays, documentation issues). This action will be logged for audit purposes.
                </p>
              </div>

              <div>
                <Label className="text-white/80 text-sm">Override Reason *</Label>
                <Textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Enter detailed reason for override..."
                  rows={3}
                  className="mt-2 bg-white/10 border-white/20 text-white"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowRegoOverrideModal(false); setSelectedVehicle(null); setOverrideReason(''); }} 
              className="border-white/20 w-full sm:w-auto h-10"
            >
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                if (!overrideReason.trim()) {
                  toast.error('Please provide a reason for the override');
                  return;
                }
                try {
                  await api.post(`/platform-owner/vehicles/${selectedVehicle.vehicle_id}/rego-override`, {
                    reason: overrideReason,
                    action: 'extend_grace'  // Temporarily allow the vehicle to remain listed
                  });
                  toast.success('Registration override applied');
                  setShowRegoOverrideModal(false);
                  setSelectedVehicle(null);
                  setOverrideReason('');
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to apply override');
                }
              }}
              className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto h-10"
              disabled={!overrideReason.trim()}
              data-testid="confirm-rego-override-btn"
            >
              Apply Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0: Dispute Detail Modal */}
      <Dialog open={showDisputeDetailModal} onOpenChange={setShowDisputeDetailModal}>
        <DialogContent className="bg-slate-900 border-white/20 text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              Dispute #{selectedDispute?.dispute_id?.slice(0, 12)}
            </DialogTitle>
          </DialogHeader>
          {selectedDispute && (
            <div className="py-4 space-y-4">
              {/* Status and Type */}
              <div className="flex items-center gap-2">
                <Badge className={`text-xs ${
                  selectedDispute.status === 'open' ? 'bg-red-500/20 text-red-400' :
                  selectedDispute.status === 'under_review' ? 'bg-yellow-500/20 text-yellow-400' :
                  selectedDispute.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {selectedDispute.status?.replace('_', ' ').toUpperCase()}
                </Badge>
                <Badge variant="outline" className="border-white/20 text-white/60 text-xs">
                  {selectedDispute.type || 'general'}
                </Badge>
                {selectedDispute.disputed_amount && (
                  <Badge className="bg-orange-500/20 text-orange-400 text-xs">
                    ${selectedDispute.disputed_amount}
                  </Badge>
                )}
              </div>

              {/* Parties */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-white/50 mb-1">Raised By</p>
                  <p className="text-sm font-medium">{selectedDispute.raised_by_user?.full_name}</p>
                  <p className="text-xs text-white/60">{selectedDispute.raised_by_user?.email}</p>
                  <Badge variant="outline" className="mt-1 text-xs border-white/20">
                    {selectedDispute.raised_by_role}
                  </Badge>
                </div>
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-white/50 mb-1">Against</p>
                  <p className="text-sm font-medium">{selectedDispute.against_user?.full_name}</p>
                  <p className="text-xs text-white/60">{selectedDispute.against_user?.email}</p>
                </div>
              </div>

              {/* Reason & Explanation */}
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-white/50 mb-1">Reason</p>
                <p className="text-sm font-medium">{selectedDispute.reason}</p>
                <p className="text-xs text-white/50 mt-3 mb-1">Explanation</p>
                <p className="text-sm text-white/80">{selectedDispute.explanation}</p>
              </div>

              {/* Booking Info */}
              {selectedDispute.booking && (
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-white/50 mb-1">Related Booking</p>
                  <p className="text-sm">#{selectedDispute.booking.booking_id?.slice(0, 8)} • {selectedDispute.vehicle}</p>
                  <p className="text-xs text-white/60 mt-1">
                    {selectedDispute.booking.start_date} to {selectedDispute.booking.end_date} • ${selectedDispute.booking.total_amount}
                  </p>
                </div>
              )}

              {/* Admin Notes */}
              {selectedDispute.admin_notes?.length > 0 && (
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-white/50 mb-2">Admin Notes</p>
                  <div className="space-y-2">
                    {selectedDispute.admin_notes.map((note, idx) => (
                      <div key={idx} className="p-2 bg-white/5 rounded text-xs">
                        <p className="text-white/80">{note.text}</p>
                        <p className="text-white/40 mt-1">{note.added_by_name} • {new Date(note.added_at).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resolution (if resolved) */}
              {selectedDispute.resolution && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-xs text-green-400 mb-1">Resolution</p>
                  <p className="text-sm text-white">{selectedDispute.resolution}</p>
                  {selectedDispute.resolution_amount > 0 && (
                    <p className="text-xs text-green-400 mt-2">Refund: ${selectedDispute.resolution_amount}</p>
                  )}
                </div>
              )}

              {/* Add Note */}
              {selectedDispute.status !== 'resolved' && selectedDispute.status !== 'closed' && (
                <div className="p-3 bg-white/5 rounded-lg">
                  <Label className="text-white/80 text-sm">Add Internal Note</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="Add a note..."
                      className="bg-white/10 border-white/20 text-white flex-1"
                    />
                    <Button
                      onClick={async () => {
                        if (!adminNote.trim()) return;
                        try {
                          await api.post(`/admin/disputes/${selectedDispute.dispute_id}/add-note`, { note: adminNote });
                          toast.success('Note added');
                          setAdminNote('');
                          fetchData();
                        } catch (e) {
                          toast.error('Failed to add note');
                        }
                      }}
                      className="bg-white/10 hover:bg-white/20"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowDisputeDetailModal(false); setSelectedDispute(null); }} 
              className="border-white/20 w-full sm:w-auto h-10"
            >
              Close
            </Button>
            {selectedDispute?.status === 'open' && (
              <Button 
                onClick={async () => {
                  try {
                    await api.post(`/admin/disputes/${selectedDispute.dispute_id}/update-status`, { status: 'under_review' });
                    toast.success('Dispute marked as under review');
                    setShowDisputeDetailModal(false);
                    fetchData();
                  } catch (e) {
                    toast.error('Failed to update status');
                  }
                }}
                className="bg-yellow-600 hover:bg-yellow-700 w-full sm:w-auto h-10"
              >
                Start Review
              </Button>
            )}
            {selectedDispute?.status !== 'resolved' && selectedDispute?.status !== 'closed' && (
              <Button 
                onClick={() => { setShowResolveModal(true); }}
                className="bg-green-600 hover:bg-green-700 w-full sm:w-auto h-10"
                data-testid="resolve-dispute-btn"
              >
                Resolve Dispute
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0: Resolve Dispute Modal */}
      <Dialog open={showResolveModal} onOpenChange={setShowResolveModal}>
        <DialogContent className="bg-slate-900 border-white/20 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Resolve Dispute
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-white/80 text-sm">Resolution Favor *</Label>
              <select
                value={resolutionFavor}
                onChange={(e) => setResolutionFavor(e.target.value)}
                className="w-full mt-2 h-10 rounded-md bg-white/10 border border-white/20 px-3 text-white text-sm"
              >
                <option value="customer">In favor of Customer</option>
                <option value="owner">In favor of Host</option>
                <option value="partial">Partial/Compromise</option>
                <option value="no_action">No Action Required</option>
              </select>
            </div>
            <div>
              <Label className="text-white/80 text-sm">Resolution Details * (min 20 characters)</Label>
              <Textarea
                value={resolutionText}
                onChange={(e) => setResolutionText(e.target.value)}
                placeholder="Explain the resolution and reasoning..."
                className="mt-2 bg-white/10 border-white/20 text-white"
                rows={4}
              />
            </div>
            <div>
              <Label className="text-white/80 text-sm">Refund Amount (optional)</Label>
              <div className="relative mt-2">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40">$</span>
                <Input
                  type="number"
                  value={resolutionAmount}
                  onChange={(e) => setResolutionAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-7 bg-white/10 border-white/20 text-white"
                  min="0"
                  step="0.01"
                />
              </div>
              <p className="text-xs text-white/50 mt-1">Leave empty if no refund is needed</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowResolveModal(false); setResolutionText(''); setResolutionAmount(''); setResolutionFavor('partial'); }} 
              className="border-white/20 w-full sm:w-auto h-10"
            >
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                if (resolutionText.length < 20) {
                  toast.error('Resolution details must be at least 20 characters');
                  return;
                }
                try {
                  await api.post(`/admin/disputes/${selectedDispute.dispute_id}/resolve`, {
                    resolution: resolutionText,
                    resolution_amount: resolutionAmount ? parseFloat(resolutionAmount) : null,
                    favor: resolutionFavor
                  });
                  toast.success('Dispute resolved successfully');
                  setShowResolveModal(false);
                  setShowDisputeDetailModal(false);
                  setSelectedDispute(null);
                  setResolutionText('');
                  setResolutionAmount('');
                  setResolutionFavor('partial');
                  fetchData();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to resolve dispute');
                }
              }}
              className="bg-green-600 hover:bg-green-700 w-full sm:w-auto h-10"
              disabled={resolutionText.length < 20}
              data-testid="confirm-resolve-btn"
            >
              Confirm Resolution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User/Admin Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-slate-900 border-white/20 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete {deleteTarget?.type === 'admin' ? 'Admin' : 'User'}
            </DialogTitle>
          </DialogHeader>
          
          {deleteTarget && (
            <div className="space-y-4 py-4">
              {/* Preview-Only Warning Banner */}
              {deleteEligibility?.is_preview_environment && (
                <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-xs text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    <span className="font-medium">Preview Environment</span> - Extended delete permissions active
                  </p>
                </div>
              )}
              
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="font-medium">{deleteTarget.full_name}</p>
                <p className="text-sm text-white/60">{deleteTarget.email}</p>
                {deleteEligibility?.is_preview_account && (
                  <Badge variant="outline" className="mt-2 text-xs border-blue-500/50 text-blue-400">
                    Preview-created account
                  </Badge>
                )}
              </div>
              
              {deleteEligibility ? (
                <>
                  {deleteEligibility.can_delete ? (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-2 text-green-400 mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-medium text-sm">Eligible for deletion</span>
                      </div>
                      <p className="text-xs text-white/60">
                        {deleteEligibility.is_preview_environment 
                          ? 'This preview account can be safely deleted. All associated data will be removed. Email can be reused.'
                          : 'This test user can be safely deleted. All associated data will be removed.'
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="flex items-center gap-2 text-red-400 mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="font-medium text-sm">Cannot delete</span>
                      </div>
                      <ul className="text-xs text-white/60 space-y-1">
                        {deleteEligibility.reasons?.map((reason, i) => (
                          <li key={i}>• {reason}</li>
                        ))}
                      </ul>
                      {deleteEligibility.suggestion === 'disable' && (
                        <p className="text-xs text-amber-400 mt-2">
                          Suggestion: Disable the account instead
                        </p>
                      )}
                    </div>
                  )}
                  
                  {deleteEligibility.can_delete && (
                    <div className="space-y-2">
                      <Label className="text-sm text-white/80">
                        Type <span className="text-red-400 font-mono">DELETE</span> to confirm:
                      </Label>
                      <Input
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="DELETE"
                        className="bg-white/10 border-white/20 text-white font-mono"
                        data-testid="delete-confirm-input"
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="flex justify-center py-4">
                  <RefreshCw className="w-6 h-6 animate-spin text-white/40" />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteModal(false)}
              className="border-white/20 text-white hover:bg-white/10 w-full sm:w-auto"
            >
              Cancel
            </Button>
            {deleteEligibility?.can_delete ? (
              <Button 
                variant="destructive"
                onClick={handleDeleteUser}
                disabled={deleteConfirmText !== 'DELETE' || deleteLoading}
                className="w-full sm:w-auto"
                data-testid="confirm-delete-btn"
              >
                {deleteLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" />Delete Permanently</>
                )}
              </Button>
            ) : deleteEligibility?.suggestion === 'disable' ? (
              <Button 
                variant="outline"
                onClick={() => handleDisableUser(deleteTarget?.user_id)}
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 w-full sm:w-auto"
              >
                <Power className="w-4 h-4 mr-2" />
                Disable Instead
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ResponsiveLayout>

    {/* Step-Up Auth Modal — gates sensitive actions */}
    <StepUpAuthModal
      isOpen={showStepUp}
      onSuccess={handleStepUpSuccess}
      onCancel={() => { setShowStepUp(false); setStepUpAction(null); }}
      actionLabel={stepUpAction?.label || 'perform this action'}
    />
    </>
  );
};

export default PlatformOwnerDashboard;
